/**
 * Product Cart Manager v2.0
 * 
 * Refactored with:
 * - Pre-calculated pending cart state
 * - Bulk cart submission
 * - Haptic feedback
 * - Enhanced line item metadata
 * - Unified Add to Cart / Buy Now flow
 * 
 * Dependencies:
 * - Flatpickr (date picker)
 * - Supabase (shipping locations & blocked dates)
 * - Shopify Cart API
 */

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Show a toast notification
 */
function showToast(message = "Item(s) added to cart!", type = "success") {
  const notice = document.createElement("div");
  notice.innerText = message;
  notice.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background-color: ${type === "error" ? "#dc2626" : "#1c1c1c"};
    color: #fff;
    padding: 12px 20px;
    border-radius: 8px;
    font-size: 14px;
    z-index: 9999;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    opacity: 0.95;
    transition: opacity 0.3s ease;
  `;
  document.body.appendChild(notice);

  setTimeout(() => {
    notice.style.opacity = "0";
    setTimeout(() => notice.remove(), 300);
  }, 3000);
}

/**
 * Trigger haptic feedback if supported
 */
function triggerHaptic(pattern = "light") {
  if (!navigator.vibrate) return;
  
  const patterns = {
    light: [10],
    medium: [20],
    heavy: [30],
    success: [10, 50, 10],
    error: [50, 30, 50, 30, 50],
    preparing: [5, 30, 5, 30, 5]
  };
  
  navigator.vibrate(patterns[pattern] || patterns.light);
}

/**
 * Format price in PHP
 */
function formatPrice(amount) {
  return `₱${amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

let activeAllergenPrompt = null;
const ALLERGEN_DECLARATION_VERSION = '2026-07-30';
const ALLERGEN_ACKNOWLEDGMENT_KEY = 'Allergen Acknowledgment';
const ALLERGEN_DECLARATION_VERSION_KEY = 'Allergen Declaration Version';
const ALLERGEN_ACKNOWLEDGED_AT_KEY = 'Allergen Acknowledged At';

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

// ============================================================================
// PRODUCT CART MANAGER CLASS
// ============================================================================

class ProductCartManager {
  constructor(config) {
    this.config = config;
    
    // DOM Elements
    this.dateInput = document.querySelector("#delivery-dates");
    this.addToCartBtn = document.querySelector('form[action="/cart/add"] [type="submit"]');
    this.buyNowBtn = document.querySelector('.custom-buy-button');
    this.quantityInput = document.querySelector('input[name="quantity"]');
    this.productForm = document.querySelector('form[action="/cart/add"]');
    this.deliveryLocationSelect = document.querySelector('#delivery-location-select');
    this.purchaseReasonSelect = document.querySelector('#purchase-reason-select');

    console.log('🔍 DOM Elements:', {
      dateInput: !!this.dateInput,
      addToCartBtn: !!this.addToCartBtn,
      buyNowBtn: !!this.buyNowBtn,
      quantityInput: !!this.quantityInput,
      deliveryLocationSelect: !!this.deliveryLocationSelect,
      purchaseReasonSelect: !!this.purchaseReasonSelect
    });
    
    // State
    this.shippingLocations = [];
    // Map of location_slug -> delivery_timing_capability ('same_day' | 'day_before' | 'both')
    // Built once after fetch, used as single source of truth for period filtering.
    this.locationCapabilityMap = {};
    this.blockedDateSet = new Set();
    // Map of date string (YYYY-MM-DD) -> allowed_timing ('same_day' | 'day_before' | 'both')
    // Fetched from delivery_schedule_period_overrides. Defaults to 'both' when no entry exists.
    this.periodOverrideMap = {};
    this.selectedDates = [];
    this.cartHasItems = false;
    this.addedToCartThisSession = false;
    this.isSubmitting = false;
    this.isAllergenPromptOpen = false;
    this.savedDeliveryLocation = null; // Preserved across variant changes
    
    // Cutoff state (Manila time)
    this.past2pm = false;
    this.past5pm = false;
    
    // Pending cart state - pre-calculated before submission
    this.pendingCart = {
      items: [],           // Array of { type: 'meal' | 'shipping', ... }
      mealSubtotal: 0,
      shippingTotal: 0,
      grandTotal: 0,
      dateCount: 0,
      quantity: 0
    };
    
    // Initialize if elements exist
    if (this.dateInput && this.addToCartBtn && this.quantityInput) {
      this.init();
    }
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  async init() {
    console.log('🚀 ProductCartManager v2.0 initializing...');
    
    // Fetch data from Supabase
    await Promise.all([
      this.fetchShippingLocations(),
      this.fetchBlockedDates(),
      this.fetchPeriodOverrides(),
      this.fetchPurchaseReasons()
    ]);
    
    // Initialize UI
    this.initializeFieldStates();
    this.initializeFlatpickr();
    
    // Bind events
    this.bindEvents();
    
    // Subscribe to Shopify's variant change event to handle section refresh
    this.subscribeToVariantChange();
    
    // Check existing cart state
    await this.refreshCartState();
    
    // Initial UI update
    this.recalculatePendingCart();
    this.updateUI();
    
    console.log('✅ ProductCartManager initialized');
  }

  /**
   * Subscribe to Shopify's variant change pub/sub event
   * This handles re-initialization after section refresh
   */
  subscribeToVariantChange() {
    // Listen for option value changes (fires BEFORE DOM update) to save state
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
        // Save the current delivery location before section refresh
        if (this.deliveryLocationSelect && this.deliveryLocationSelect.value) {
          this.savedDeliveryLocation = this.deliveryLocationSelect.value;
          console.log('💾 Saved delivery location:', this.savedDeliveryLocation);
        }
      });
      
      // Listen for variant change (fires AFTER DOM update) to restore state
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        console.log('🔄 Variant changed, re-initializing delivery location...');
        // Use requestAnimationFrame to ensure DOM is fully updated
        requestAnimationFrame(() => {
          this.handleVariantSectionRefresh();
        });
      });
      
      console.log('📡 Subscribed to Shopify variant change events');
    } else {
      console.warn('⚠️ Shopify pub/sub not available, variant change handling disabled');
    }
  }

  /**
   * Populate delivery location dropdown from in-memory locations.
   * Used after section refresh (variant change) when the select is replaced with server HTML and has no options.
   */
  populateDeliveryLocationDropdown() {
    this.deliveryLocationSelect = document.querySelector('#delivery-location-select');
    if (!this.deliveryLocationSelect || !this.shippingLocations?.length) return;

    // Keep only the first (placeholder) option, clear the rest
    while (this.deliveryLocationSelect.options.length > 1) {
      this.deliveryLocationSelect.remove(1);
    }

    this.shippingLocations.forEach(location => {
      const option = document.createElement('option');
      option.value = location.location_slug;
      option.textContent = `${location.location_name} - ₱${(location.price_in_cents / 100).toFixed(2)}`;
      option.dataset.price = location.price_in_cents;
      option.dataset.locationId = location.id;
      option.dataset.locationName = location.location_name;
      option.dataset.variantId = location.shopify_variant_id || '';
      option.dataset.timingCapability = location.delivery_timing_capability || 'both';
      this.deliveryLocationSelect.appendChild(option);
    });
  }

  /**
   * Handle section refresh after variant change
   * Re-populates delivery location options (section swap replaces the select with server HTML) and restores selection.
   */
  handleVariantSectionRefresh() {
    // Re-query the delivery location select (element reference may be stale after section swap)
    this.deliveryLocationSelect = document.querySelector('#delivery-location-select');
    // Reset snapshot so applyLocationCapabilityFilter re-captures from fresh server HTML
    this._allDeliveryPeriodOptions = null;

    // Section refresh replaces variant-selects (including the delivery dropdown) with server HTML that has no options.
    // Re-populate from in-memory locations so the dropdown has choices again.
    this.populateDeliveryLocationDropdown();

    // Restore the saved delivery location value
    if (this.savedDeliveryLocation && this.deliveryLocationSelect) {
      const optionExists = Array.from(this.deliveryLocationSelect.options).some(
        opt => opt.value === this.savedDeliveryLocation
      );

      if (optionExists) {
        this.deliveryLocationSelect.value = this.savedDeliveryLocation;
        console.log('✅ Restored delivery location:', this.savedDeliveryLocation);

        const refDate = this.effectiveMinDate;
        this.handleLocationChange();
        this.applyLocationCapabilityFilter();
        this.applyDatePeriodOverrideFilter(refDate);
        this.applyDeliveryPeriodCutoff(refDate);
        this.recalculatePendingCart();
        this.updateUI();
      }
    }

    // Re-bind the delivery location change event
    if (this.deliveryLocationSelect) {
      // Remove existing listener to avoid duplicates
      this.deliveryLocationSelect.removeEventListener('change', this.boundLocationChangeHandler);
      
      // Create bound handler if not exists
      if (!this.boundLocationChangeHandler) {
        this.boundLocationChangeHandler = (e) => {
          e.stopPropagation();
          this.handleLocationChange();
          this.recalculatePendingCart();
          this.updateUI();
        };
      }
      
      this.deliveryLocationSelect.addEventListener('change', this.boundLocationChangeHandler);
    }
  }

  // ==========================================================================
  // DATA FETCHING (Supabase)
  // ==========================================================================

  async fetchShippingLocations() {
    const loadingEl = document.querySelector('#delivery-location-loading');
    const errorEl = document.querySelector('#delivery-location-error');
    
    if (!this.deliveryLocationSelect) return;
    if (loadingEl) loadingEl.style.display = 'block';
    
    try {
      const response = await fetch(
        `${this.config.supabaseUrl}/rest/v1/shipping_locations?select=*&is_active=eq.true&order=location_name.asc`,
        {
          headers: {
            'apikey': this.config.supabaseKey,
            'Authorization': `Bearer ${this.config.supabaseKey}`
          }
        }
      );
      
      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
      
      this.shippingLocations = await response.json();
      // Build capability map: slug -> capability
      this.locationCapabilityMap = {};
      this.shippingLocations.forEach(loc => {
        this.locationCapabilityMap[loc.location_slug] = loc.delivery_timing_capability || 'both';
      });
      console.log('✅ Fetched shipping locations:', this.shippingLocations.length, '| capability map:', this.locationCapabilityMap);
      
      // Populate dropdown
      this.shippingLocations.forEach(location => {
        const option = document.createElement('option');
        option.value = location.location_slug;
        option.textContent = `${location.location_name} - ₱${(location.price_in_cents / 100).toFixed(2)}`;
        option.dataset.price = location.price_in_cents;
        option.dataset.locationId = location.id;
        option.dataset.locationName = location.location_name;
        option.dataset.variantId = location.shopify_variant_id || '';
        option.dataset.timingCapability = location.delivery_timing_capability || 'both';
        this.deliveryLocationSelect.appendChild(option);
      });
      
      if (loadingEl) loadingEl.style.display = 'none';
    } catch (error) {
      console.error('❌ Failed to fetch shipping locations:', error);
      if (loadingEl) loadingEl.style.display = 'none';
      if (errorEl) errorEl.style.display = 'block';
    }
  }

  async fetchPurchaseReasons() {
    const loadingEl = document.querySelector('#purchase-reason-loading');
    const errorEl = document.querySelector('#purchase-reason-error');

    if (!this.purchaseReasonSelect) return;
    if (loadingEl) loadingEl.style.display = 'block';

    try {
      const response = await fetch(
        `${this.config.supabaseUrl}/rest/v1/purchase_reasons?select=label,slug&is_active=eq.true&order=sort_order.asc`,
        {
          headers: {
            'apikey': this.config.supabaseKey,
            'Authorization': `Bearer ${this.config.supabaseKey}`
          }
        }
      );

      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

      const reasons = await response.json();
      console.log('✅ Fetched purchase reasons:', reasons.length);

      reasons.forEach(reason => {
        const option = document.createElement('option');
        option.value = reason.label;
        option.textContent = reason.label;
        this.purchaseReasonSelect.appendChild(option);
      });

      if (loadingEl) loadingEl.style.display = 'none';
    } catch (error) {
      console.error('❌ Failed to fetch purchase reasons:', error);
      if (loadingEl) loadingEl.style.display = 'none';
      if (errorEl) errorEl.style.display = 'block';
    }
  }

  async fetchBlockedDates() {
    try {
      const response = await fetch(
        `${this.config.supabaseUrl}/rest/v1/delivery_schedule_blocks?select=blocked_date`,
        {
          headers: {
            'apikey': this.config.supabaseKey,
            'Authorization': `Bearer ${this.config.supabaseKey}`
          }
        }
      );
      
      if (!response.ok) throw new Error(`Supabase fetch failed: ${response.status}`);
      
      const data = await response.json();
      this.blockedDateSet = new Set(data.map(item => item.blocked_date));
      console.log('✅ Fetched blocked dates:', this.blockedDateSet.size);
    } catch (error) {
      console.warn('⚠️ Failed to fetch blocked dates:', error);
      this.blockedDateSet = new Set();
    }
  }

  async fetchPeriodOverrides() {
    try {
      const response = await fetch(
        `${this.config.supabaseUrl}/rest/v1/delivery_schedule_period_overrides?select=target_date,allowed_timing`,
        {
          headers: {
            'apikey': this.config.supabaseKey,
            'Authorization': `Bearer ${this.config.supabaseKey}`
          }
        }
      );

      if (!response.ok) throw new Error(`Supabase fetch failed: ${response.status}`);

      const data = await response.json();
      this.periodOverrideMap = {};
      data.forEach(item => {
        this.periodOverrideMap[item.target_date] = item.allowed_timing;
      });
      console.log('✅ Fetched period overrides:', Object.keys(this.periodOverrideMap).length);
    } catch (error) {
      console.warn('⚠️ Failed to fetch period overrides:', error);
      this.periodOverrideMap = {};
    }
  }

  async refreshCartState() {
    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      if (!res.ok) throw new Error('Cart fetch failed');
      const data = await res.json();
      this.cartHasItems = (data.item_count && data.item_count > 0) || false;
      this.currentCart = data; // Store full cart data
    } catch (err) {
      console.warn('Could not fetch cart state', err);
      this.cartHasItems = false;
      this.currentCart = { items: [] };
    }
  }

  /**
   * Fetch current cart data (fresh)
   */
  async fetchCurrentCart() {
    const res = await fetch('/cart.js', { cache: 'no-store' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return await res.json();
  }

  /**
   * Get the delivery location from existing cart items
   * Returns null if cart is empty or has no location set
   */
  getCartDeliveryLocation(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return null;
    
    // Find first meal item (not shipping)
    const mealItem = cart.items.find(item => 
      !item.properties || item.properties._shipfee !== "true"
    );
    
    if (mealItem && mealItem.properties?.['Delivery Location']) {
      return {
        slug: mealItem.properties['Delivery Location'],
        name: mealItem.properties['Delivery Location Name'] || mealItem.properties['Delivery Location']
      };
    }
    
    return null;
  }

  /**
   * Get existing shipping dates from cart for a specific location
   */
  getExistingShippingDates(cart, locationSlug) {
    const existingDates = new Set();
    
    cart.items.forEach(item => {
      if (item.properties?._shipfee === "true" && 
          item.properties?.['Delivery Location'] === locationSlug) {
        const date = item.properties['Delivery Date'];
        if (date) existingDates.add(date);
      }
    });
    
    return existingDates;
  }

  // ==========================================================================
  // LOCATION MISMATCH DIALOG (Phase 5)
  // ==========================================================================

  /**
   * Show location mismatch dialog and return user's choice
   * @param {Object} cartLocation - Current cart location { slug, name }
   * @param {Object} newLocation - New item location { slug, name }
   * @returns {Promise<string>} - 'keep-existing' | 'change-all' | 'cancel'
   */
  showLocationMismatchDialog(cartLocation, newLocation) {
    return new Promise((resolve) => {
      // Create dialog overlay
      const overlay = document.createElement('div');
      overlay.className = 'location-dialog-overlay';
      overlay.innerHTML = `
        <div class="location-dialog-content">
          <div class="location-dialog-icon">⚠️</div>
          <h3 class="location-dialog-title">Different Delivery Location</h3>
          
          <div class="location-dialog-locations">
            <div class="location-dialog-row">
              <span class="location-dialog-label">Your cart</span>
              <span class="location-dialog-value">📍 ${cartLocation.name}</span>
            </div>
            <div class="location-dialog-row">
              <span class="location-dialog-label">New items</span>
              <span class="location-dialog-value">📍 ${newLocation.name}</span>
            </div>
          </div>
          
          <p class="location-dialog-message">All items must be delivered to the same location. What would you like to do?</p>
          
          <div class="location-dialog-actions">
            <button type="button" class="location-dialog-btn location-dialog-btn--primary" data-action="keep-existing">
              Keep ${cartLocation.name}
            </button>
            <button type="button" class="location-dialog-btn location-dialog-btn--secondary" data-action="change-all">
              Change all to ${newLocation.name}
            </button>
          </div>
          
          <button type="button" class="location-dialog-cancel" data-action="cancel">Cancel</button>
        </div>
      `;
      
      // Add styles
      const style = document.createElement('style');
      style.textContent = `
        .location-dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: locationDialogFadeIn 0.2s ease-out;
        }
        @keyframes locationDialogFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .location-dialog-content {
          background: #ffffff;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          text-align: center;
          max-width: 420px;
          width: 90%;
          animation: locationDialogSlideIn 0.2s ease-out;
        }
        @keyframes locationDialogSlideIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .location-dialog-icon {
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }
        .location-dialog-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 1.25rem 0;
        }
        .location-dialog-locations {
          background: #f9fafb;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.25rem;
        }
        .location-dialog-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 0;
        }
        .location-dialog-row:first-child {
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 0.75rem;
          margin-bottom: 0.5rem;
        }
        .location-dialog-label {
          font-size: 0.75rem;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .location-dialog-value {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #1a1a1a;
        }
        .location-dialog-message {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 1.5rem 0;
          line-height: 1.5;
        }
        .location-dialog-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .location-dialog-btn {
          width: 100%;
          padding: 0.875rem 1.25rem;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          border: none;
        }
        .location-dialog-btn--primary {
          background: #1a1a1a;
          color: #ffffff;
        }
        .location-dialog-btn--primary:hover {
          background: #333333;
        }
        .location-dialog-btn--secondary {
          background: #f3f4f6;
          color: #1a1a1a;
          border: 1px solid #e5e7eb;
        }
        .location-dialog-btn--secondary:hover {
          background: #e5e7eb;
        }
        .location-dialog-cancel {
          margin-top: 0.5rem;
          font-size: 0.875rem;
          color: #6b7280;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.5rem;
        }
        .location-dialog-cancel:hover {
          color: #1a1a1a;
          text-decoration: underline;
        }
      `;
      
      document.head.appendChild(style);
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';
      
      // Handle button clicks
      const cleanup = () => {
        document.body.style.overflow = '';
        overlay.remove();
        style.remove();
      };
      
      overlay.addEventListener('click', (e) => {
        const action = e.target.dataset.action;
        if (action) {
          cleanup();
          resolve(action);
        }
      });
    });
  }

  /**
   * Update all cart items to a new delivery location
   * @param {Object} cart - Current cart data
   * @param {Object} newLocation - New location info
   */
  async updateCartToNewLocation(cart, newLocation) {
    console.log('🔄 Updating cart to new location:', newLocation.name);
    
    // Show loading state on button
    this.setButtonState('preparing');
    this.addToCartBtn.textContent = 'Updating cart...';
    
    try {
      // Get meal items (not shipping)
      const mealItems = cart.items.filter(item => 
        !item.properties || item.properties._shipfee !== "true"
      );
      
      if (mealItems.length === 0) return;
      
      // Build items with new location and deduplicated shipping
      const itemsToAdd = [];
      const uniqueDates = new Set();
      
      mealItems.forEach(item => {
        const deliveryDate = item.properties?.['Selected Consumption Dates'] || '';
        
        // Meal item with new location
        itemsToAdd.push({
          id: item.variant_id,
          quantity: item.quantity,
          properties: {
            ...item.properties,
            'Delivery Location': newLocation.slug,
            'Delivery Location Name': newLocation.name,
            'Shipping Fee': formatPrice(newLocation.price)
          }
        });
        
        if (deliveryDate) uniqueDates.add(deliveryDate);
      });
      
      // Add ONE shipping per unique date
      if (newLocation.variantId) {
        uniqueDates.forEach(date => {
          itemsToAdd.push({
            id: newLocation.variantId,
            quantity: 1,
            properties: {
              _shipfee: "true",
              "Delivery Date": date,
              "Delivery Location": newLocation.slug,
              "Delivery Location Name": newLocation.name,
              "Linked Products": "Multiple products"
            }
          });
        });
      }
      
      // Clear cart
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Add all items in bulk
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });
      
      console.log('✅ Cart updated to new location');
      
    } catch (error) {
      console.error('❌ Failed to update cart location:', error);
      throw error;
    }
  }

  // ==========================================================================
  // UI INITIALIZATION
  // ==========================================================================

  initializeFieldStates() {
    const variantSelects = document.querySelectorAll('[id^="Option-"]');
    const quantityForm = document.querySelector(`#Quantity-Form-${this.config.sectionId}`);
    const dateInputWrapper = document.querySelector('.product__delivery-dates');
    
    // Disable 3rd+ variant options initially
    variantSelects.forEach((select, index) => {
      if (index >= 2) {
        this.setFieldDisabled(select, true);
      }
    });
    
    // Disable quantity and dates initially
    if (quantityForm) quantityForm.classList.add('quantity-disabled');
    if (dateInputWrapper) dateInputWrapper.style.opacity = '0.4';
    if (this.quantityInput) this.quantityInput.disabled = true;
    if (this.dateInput) this.dateInput.disabled = true;
    if (this.addToCartBtn) this.addToCartBtn.disabled = true;
    
    document.querySelectorAll('.quantity__button').forEach(btn => btn.disabled = true);
  }

  setFieldDisabled(element, disabled) {
    if (!element) return;
    element.disabled = disabled;
    element.style.opacity = disabled ? '0.5' : '1';
    element.style.cursor = disabled ? 'not-allowed' : 'pointer';
    
    const wrapper = element.closest('.product-form__input');
    if (wrapper) {
      wrapper.classList.toggle('product-form__input--disabled', disabled);
    }
  }

  /**
   * Restrict delivery period options based on the selected location's timing capability.
   * Uses this.locationCapabilityMap (keyed by slug) — no DOM dataset reads.
   * Runs BEFORE applyDeliveryPeriodCutoff.
   *
   * NOTE: We physically remove incompatible options from the DOM so the native OS
   * picker never shows them. They are stored in this._allDeliveryPeriodOptions and
   * re-inserted when the location changes to one that allows them.
   *
   * capability = 'same_day'   → remove all "Day Before" options
   * capability = 'day_before' → remove all "Same Day" options
   * capability = 'both'       → restore all options
   */
  applyLocationCapabilityFilter() {
    const deliveryPeriodSelect = document.querySelector(`#Option-${this.config.sectionId}-2`);
    if (!deliveryPeriodSelect) return;

    // Snapshot all options the first time we run so we can restore them later
    if (!this._allDeliveryPeriodOptions) {
      this._allDeliveryPeriodOptions = Array.from(deliveryPeriodSelect.options).map(o => o.cloneNode(true));
    }

    const slug = this.deliveryLocationSelect?.value || '';
    const capability = this.locationCapabilityMap[slug] || 'both';

    // Restore full option list first, then remove what's blocked
    const currentValue = deliveryPeriodSelect.value;
    deliveryPeriodSelect.innerHTML = '';
    this._allDeliveryPeriodOptions.forEach(opt => {
      const clone = opt.cloneNode(true);
      clone.dataset.capabilityDisabled = '';
      deliveryPeriodSelect.appendChild(clone);
    });

    if (capability !== 'both') {
      Array.from(deliveryPeriodSelect.options).forEach(opt => {
        if (!opt.value) return;
        const val = opt.value.toUpperCase();
        const isDayBefore = val.includes('DAY BEFORE') || val.includes('DAY-BEFORE');
        const isSameDay   = val.includes('SAME DAY')   || val.includes('SAME-DAY');
        const blocked =
          (capability === 'same_day'   && isDayBefore) ||
          (capability === 'day_before' && isSameDay);
        if (blocked) {
          opt.dataset.capabilityDisabled = '1';
          opt.remove();
        }
      });
    }

    // Restore previous selection if it still exists, otherwise pick first valid
    const stillExists = Array.from(deliveryPeriodSelect.options).find(o => o.value === currentValue);
    if (stillExists) {
      deliveryPeriodSelect.value = currentValue;
    } else {
      const firstValid = Array.from(deliveryPeriodSelect.options).find(o => o.value);
      if (firstValid) {
        deliveryPeriodSelect.value = firstValid.value;
        deliveryPeriodSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    console.log(`📍 Capability filter | slug: "${slug}" | capability: "${capability}"`);
  }

  /**
   * Apply per-date period override filter.
   * Runs AFTER applyLocationCapabilityFilter, BEFORE applyDeliveryPeriodCutoff.
   *
   * Reads this.periodOverrideMap[dateString] for the reference date.
   * Defaults to 'both' when no override exists.
   *
   * Intersection logic:
   *   location capability  ∩  date override  =  effective allowed options
   *
   * If the intersection is empty (e.g. location='same_day', override='day_before'),
   * the dropdown is fully disabled and a note is shown. hasValidOrder() also returns
   * false, blocking Add to Cart / Checkout.
   *
   * @param {Date|null} referenceDate - The date to check overrides for. Uses effectiveMinDate when null.
   * @returns {boolean} true when at least one option remains enabled after the filter.
   */
  applyDatePeriodOverrideFilter(referenceDate) {
    const deliveryPeriodSelect = document.querySelector(`#Option-${this.config.sectionId}-2`);
    if (!deliveryPeriodSelect) return true;

    const ref = referenceDate || this.effectiveMinDate;
    if (!ref) return true;

    // Format reference date as YYYY-MM-DD in Manila timezone
    const phFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Manila',
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
    const parts = Object.fromEntries(phFmt.formatToParts(ref).map(p => [p.type, p.value]));
    const dateKey = `${parts.year}-${parts.month}-${parts.day}`;

    const overrideTiming = this.periodOverrideMap[dateKey] || 'both';

    const wrapper = deliveryPeriodSelect.closest('.product-form__input');
    const noteId = 'override-period-note';
    const existingNote = wrapper
      ? wrapper.querySelector(`#${noteId}`)
      : document.querySelector(`#${noteId}`);
    if (existingNote) existingNote.remove();

    if (overrideTiming === 'both') {
      // No restriction from override — leave options as-is from capability filter
      console.log(`📅 Period override | date: "${dateKey}" | override: "both" (no restriction)`);
      return true;
    }

    // Remove options that the override disallows (operating on whatever capability filter left)
    let removedCount = 0;
    Array.from(deliveryPeriodSelect.options).forEach(opt => {
      if (!opt.value) return;
      const val = opt.value.toUpperCase();
      const isDayBefore = val.includes('DAY BEFORE') || val.includes('DAY-BEFORE');
      const isSameDay   = val.includes('SAME DAY')   || val.includes('SAME-DAY');
      const blockedByOverride =
        (overrideTiming === 'same_day'   && isDayBefore) ||
        (overrideTiming === 'day_before' && isSameDay);

      if (blockedByOverride) {
        opt.dataset.overrideDisabled = '1';
        opt.remove();
        removedCount++;
      }
    });

    const remainingOptions = Array.from(deliveryPeriodSelect.options).filter(o => o.value);
    const hasValidOptions = remainingOptions.length > 0;

    if (!hasValidOptions) {
      // Intersection is empty — disable the whole dropdown and show a clear message
      this.setFieldDisabled(deliveryPeriodSelect, true);
      this._periodOverrideIntersectionEmpty = true;

      if (wrapper) {
        const note = document.createElement('p');
        note.id = noteId;
        note.style.cssText = 'margin:6px 0 0;font-size:12px;color:#dc2626;line-height:1.4;';
        note.textContent = 'This delivery period is not available for the selected location and date combination.';
        wrapper.appendChild(note);
      }

      console.warn(`⛔ Period override intersection empty | date: "${dateKey}" | override: "${overrideTiming}"`);
    } else {
      this._periodOverrideIntersectionEmpty = false;
      this.setFieldDisabled(deliveryPeriodSelect, false);

      // Restore selection to first valid option if current was removed
      const currentStillExists = Array.from(deliveryPeriodSelect.options).find(
        o => o.value === deliveryPeriodSelect.value
      );
      if (!currentStillExists) {
        const firstValid = remainingOptions[0];
        if (firstValid) {
          deliveryPeriodSelect.value = firstValid.value;
          deliveryPeriodSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      if (removedCount > 0 && wrapper) {
        const note = document.createElement('p');
        note.id = noteId;
        note.style.cssText = 'margin:6px 0 0;font-size:12px;color:#b7791f;line-height:1.4;';
        const label = overrideTiming === 'same_day' ? 'Same Day' : 'Day Before';
        note.textContent = `Only ${label} delivery is available for this date.`;
        wrapper.appendChild(note);
      }

      console.log(`📅 Period override | date: "${dateKey}" | override: "${overrideTiming}" | removed: ${removedCount} option(s)`);
    }

    return hasValidOptions;
  }

  /**
   * Check if today (Manila) is in the blocked dates set and apply a hard lock if so.
   * Disables the delivery period dropdown and ordering controls with a user-facing message.
   * Called once during init and whenever the date selection changes.
   *
   * @returns {boolean} true when today is blocked.
   */
  applyTodayBlockLock() {
    const serverNow = new Date(window.__DEBUG_TIME_OVERRIDE_MS__ || this.config.serverTimeMs);
    const phFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Manila',
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
    const parts = Object.fromEntries(phFmt.formatToParts(serverNow).map(p => [p.type, p.value]));
    const todayKey = `${parts.year}-${parts.month}-${parts.day}`;

    const isTodayBlocked = this.blockedDateSet.has(todayKey);
    this._todayIsBlocked = isTodayBlocked;

    const deliveryPeriodSelect = document.querySelector(`#Option-${this.config.sectionId}-2`);
    const wrapper = deliveryPeriodSelect?.closest('.product-form__input');
    const noteId = 'today-block-note';
    const existingNote = wrapper
      ? wrapper.querySelector(`#${noteId}`)
      : document.querySelector(`#${noteId}`);

    if (isTodayBlocked) {
      if (deliveryPeriodSelect) this.setFieldDisabled(deliveryPeriodSelect, true);

      if (!existingNote && wrapper) {
        const note = document.createElement('p');
        note.id = noteId;
        note.style.cssText = 'margin:6px 0 0;font-size:12px;color:#dc2626;line-height:1.4;';
        note.textContent = 'Delivery is not available today. Please select a future date.';
        wrapper.appendChild(note);
      }

      console.warn(`🔒 Today (${todayKey}) is blocked — ordering disabled`);
    } else {
      if (existingNote) existingNote.remove();
    }

    return isTodayBlocked;
  }

  /**
   * Disable/enable delivery period dropdown options based on cutoff time and reference date.
   * - referenceDate: the earliest selectable or selected date (accounts for blocked dates).
   *   Falls back to this.effectiveMinDate when not provided.
   * - After 5 PM: min date shifts to +2 days, all options remain enabled
   * - Between 2 PM and 5 PM: "Day Before" is disabled UNLESS referenceDate is 2+ days out
   * - Before 2 PM: all options enabled
   */
  applyDeliveryPeriodCutoff(referenceDate) {
    const deliveryPeriodSelect = document.querySelector(`#Option-${this.config.sectionId}-2`);
    if (!deliveryPeriodSelect) return;

    const ref = referenceDate || this.effectiveMinDate;

    const wrapper = deliveryPeriodSelect.closest('.product-form__input');
    const noteId = 'cutoff-period-note';
    let existingNote = wrapper ? wrapper.querySelector(`#${noteId}`) : document.querySelector(`#${noteId}`);

    if (this.past5pm) {
      // After 5 PM: Calendar minDate is already +2 days (day after tomorrow).
      // For dates 2+ days out, both "DAY BEFORE" and "SAME DAY" are valid options.
      // Keep all options enabled - the calendar restriction handles the date limit.
      // Skip options already disabled by the location capability filter.
      this.setFieldDisabled(deliveryPeriodSelect, false);
      Array.from(deliveryPeriodSelect.options).forEach(opt => { if (!opt.dataset.capabilityDisabled) opt.disabled = false; });

      if (!existingNote && wrapper) {
        existingNote = document.createElement('p');
        existingNote.id = noteId;
        existingNote.style.cssText = 'margin:6px 0 0;font-size:12px;color:#047857;line-height:1.4;';
        existingNote.textContent = 'Tomorrow is no longer available. Please select dates from the day after tomorrow.';
        wrapper.appendChild(existingNote);
      } else if (existingNote) {
        existingNote.style.color = '#047857';
        existingNote.textContent = 'Tomorrow is no longer available. Please select dates from the day after tomorrow.';
      }
      return;
    }

    if (this.past2pm) {
      // Determine if the reference date is 2+ days from today (Manila).
      // If so, "Day Before" is safe to use even in the 2-5PM window.
      const serverNow = new Date(window.__DEBUG_TIME_OVERRIDE_MS__ || this.config.serverTimeMs);
      const phDateFmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Manila',
        year: 'numeric', month: '2-digit', day: '2-digit'
      });
      const todayParts = Object.fromEntries(phDateFmt.formatToParts(serverNow).map(p => [p.type, p.value]));
      const todayMidnight = new Date(+todayParts.year, +todayParts.month - 1, +todayParts.day);
      const refMidnight = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
      const daysDiff = Math.round((refMidnight - todayMidnight) / (1000 * 60 * 60 * 24));

      if (daysDiff >= 2) {
        // Reference date is 2+ days out — unlock all options not blocked by capability filter
        this.setFieldDisabled(deliveryPeriodSelect, false);
        Array.from(deliveryPeriodSelect.options).forEach(opt => { if (!opt.dataset.capabilityDisabled) opt.disabled = false; });
        if (existingNote) existingNote.remove();
        return;
      }

      Array.from(deliveryPeriodSelect.options).forEach(opt => {
        const val = opt.value.toUpperCase();
        if (val.includes('DAY BEFORE') || val.includes('DAY-BEFORE')) {
          opt.disabled = true;
          if (deliveryPeriodSelect.value === opt.value) {
            const sameDayOpt = Array.from(deliveryPeriodSelect.options).find(
              o => o.value.toUpperCase().includes('SAME DAY') && !o.disabled
            );
            if (sameDayOpt) {
              deliveryPeriodSelect.value = sameDayOpt.value;
              deliveryPeriodSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        } else if (!opt.dataset.capabilityDisabled) {
          opt.disabled = false;
        }
      });

      if (!existingNote && wrapper) {
        existingNote = document.createElement('p');
        existingNote.id = noteId;
        existingNote.style.cssText = 'margin:6px 0 0;font-size:12px;color:#b7791f;line-height:1.4;';
        existingNote.textContent = 'Day Before Consumption is no longer available after 2:00 PM.';
        wrapper.appendChild(existingNote);
      } else if (existingNote) {
        existingNote.style.color = '#b7791f';
        existingNote.textContent = 'Day Before Consumption is no longer available after 2:00 PM.';
      }
      return;
    }

    // Before 2 PM: all options enabled (except those blocked by location capability filter)
    Array.from(deliveryPeriodSelect.options).forEach(opt => { if (!opt.dataset.capabilityDisabled) opt.disabled = false; });
    if (existingNote) existingNote.remove();
  }

  initializeFlatpickr() {
    const serverNow = new Date(window.__DEBUG_TIME_OVERRIDE_MS__ || this.config.serverTimeMs);
    
    // Calculate min date based on Manila timezone and dual cutoff
    const phFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Manila',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
    const parts = Object.fromEntries(phFmt.formatToParts(serverNow).map(p => [p.type, p.value]));
    const phH = +parts.hour, phMin = +parts.minute;
    
    const CUTOFF_1_HOUR = 14, CUTOFF_1_MIN = 0;
    const CUTOFF_2_HOUR = 17, CUTOFF_2_MIN = 0;
    
    this.past2pm = (phH > CUTOFF_1_HOUR) || (phH === CUTOFF_1_HOUR && phMin >= CUTOFF_1_MIN);
    this.past5pm = (phH > CUTOFF_2_HOUR) || (phH === CUTOFF_2_HOUR && phMin >= CUTOFF_2_MIN);
    
    const rawMinDate = new Date(+parts.year, +parts.month - 1, +parts.day + (this.past5pm ? 2 : 1));
    this.effectiveMinDate = this.getEffectiveMinDate(rawMinDate);
    
    console.log(`📅 Cutoff check: ${phH}:${String(phMin).padStart(2,'0')} | Past 2PM: ${this.past2pm} | Past 5PM: ${this.past5pm} | Min date: ${rawMinDate.toDateString()} | Effective min: ${this.effectiveMinDate.toDateString()}`);
    
    // Decision pipeline (order matters):
    // 1. Location capability filter
    // 2. Date period override filter (intersection with capability)
    // 3. Cutoff time rules
    // 4. Today-block hard lock
    this.applyLocationCapabilityFilter();
    this.applyDatePeriodOverrideFilter(this.effectiveMinDate);
    this.applyDeliveryPeriodCutoff(this.effectiveMinDate);
    this.applyTodayBlockLock();

    this.flatpickrInstance = flatpickr(this.dateInput, {
      mode: "multiple",
      dateFormat: "F j, Y",
      minDate: this.effectiveMinDate,
      inline: true,
      disable: [
        date => date.getDay() === 0 || date.getDay() === 6, // Weekends
        date => this.isDateBlocked(date) // Blocked dates
      ],
      onDayCreate: (dObj, dStr, fp, dayElem) => {
        const date = dayElem.dateObj;
        if (date.getDay() === 0 || date.getDay() === 6 || this.isDateBlocked(date)) {
          dayElem.classList.add("weekend-disabled");
        }
        // Mark days that have a period override with a visual indicator
        const phFmt = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Asia/Manila',
          year: 'numeric', month: '2-digit', day: '2-digit'
        });
        const p = Object.fromEntries(phFmt.formatToParts(date).map(x => [x.type, x.value]));
        const key = `${p.year}-${p.month}-${p.day}`;
        if (this.periodOverrideMap[key] && this.periodOverrideMap[key] !== 'both') {
          dayElem.classList.add('period-override');
          dayElem.title = this.periodOverrideMap[key] === 'same_day'
            ? 'Same Day only'
            : 'Day Before only';
        }
      },
      onChange: (dates) => {
        this.selectedDates = dates;
        const earliest = dates.length ? dates.slice().sort((a, b) => a - b)[0] : null;
        const refDate = earliest || this.effectiveMinDate;
        // Decision pipeline on date change
        this.applyLocationCapabilityFilter();
        this.applyDatePeriodOverrideFilter(refDate);
        this.applyDeliveryPeriodCutoff(refDate);
        this.recalculatePendingCart();
        this.updateUI();
      }
    });
  }

  isDateBlocked(date) {
    const phFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Manila',
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
    const parts = Object.fromEntries(phFmt.formatToParts(date).map(p => [p.type, p.value]));
    return this.blockedDateSet.has(`${parts.year}-${parts.month}-${parts.day}`);
  }

  /**
   * Walk forward from rawMinDate until we find a day that is selectable:
   * not a weekend and not in blockedDateSet. Returns the first such date.
   */
  getEffectiveMinDate(rawMinDate) {
    const d = new Date(rawMinDate);
    while (d.getDay() === 0 || d.getDay() === 6 || this.isDateBlocked(d)) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  // ==========================================================================
  // EVENT BINDING
  // ==========================================================================

  bindEvents() {
    // Create bound handler for delivery location change
    // This allows us to remove/re-add the listener after variant changes
    this.boundLocationChangeHandler = (e) => {
      e.stopPropagation(); // Prevent Shopify from refreshing the product form
      this.handleLocationChange();
      this.recalculatePendingCart();
      this.updateUI();
    };
    
    // Delivery location change - IMPORTANT: Stop propagation to prevent Shopify's product-info.js from refreshing
    if (this.deliveryLocationSelect) {
      this.deliveryLocationSelect.addEventListener('change', this.boundLocationChangeHandler);
    }
    
    // Purchase reason change
    if (this.purchaseReasonSelect) {
      this.purchaseReasonSelect.addEventListener('change', () => {
        this.recalculatePendingCart();
        this.updateUI();
      });
    }

    // Quantity change
    ['input', 'change'].forEach(event => {
      this.quantityInput.addEventListener(event, () => {
        this.recalculatePendingCart();
        this.updateUI();
      });
    });
    
    // Add to Cart button
    this.addToCartBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleAddToCart(false);
    });
    
    // Buy Now / Checkout button
    if (this.buyNowBtn) {
      this.buyNowBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.buyNowBtn.classList.contains('is-disabled')) return;

        // If already added to cart and no new order configured, just redirect
        if (this.addedToCartThisSession && !this.hasValidOrder()) {
          window.location.href = '/cart';
          return;
        }

        this.handleAddToCart(true);
      });
    }
  }

  handleLocationChange() {
    const selectedOption = this.deliveryLocationSelect.options[this.deliveryLocationSelect.selectedIndex];
    const hasLocation = !!this.deliveryLocationSelect.value;
    
    const variantSelects = document.querySelectorAll('[id^="Option-"]');
    const quantityButtons = document.querySelectorAll('.quantity__button');
    const quantityForm = document.querySelector(`#Quantity-Form-${this.config.sectionId}`);
    const dateWrapper = document.querySelector('.product__delivery-dates');
    
    // Enable/disable 3rd+ variant options
    variantSelects.forEach((select, index) => {
      if (index >= 2) {
        this.setFieldDisabled(select, !hasLocation);
      }
    });
    
    // Re-apply full decision pipeline when location changes
    if (hasLocation) {
      const earliest = this.selectedDates.length
        ? this.selectedDates.slice().sort((a, b) => a - b)[0]
        : null;
      const refDate = earliest || this.effectiveMinDate;
      this.applyLocationCapabilityFilter();
      this.applyDatePeriodOverrideFilter(refDate);
      this.applyDeliveryPeriodCutoff(refDate);
    }
    
    // Enable/disable quantity
    if (quantityForm) quantityForm.classList.toggle('quantity-disabled', !hasLocation);
    if (this.quantityInput) {
      this.quantityInput.disabled = !hasLocation;
      this.quantityInput.style.opacity = hasLocation ? '1' : '0.5';
    }
    quantityButtons.forEach(btn => {
      btn.disabled = !hasLocation;
      btn.style.opacity = hasLocation ? '1' : '0.5';
    });
    
    // Enable/disable dates
    if (dateWrapper) dateWrapper.style.opacity = hasLocation ? '1' : '0.4';
    if (this.dateInput) {
      this.dateInput.disabled = !hasLocation;
      this.dateInput.style.opacity = hasLocation ? '1' : '0.5';
    }
    
    if (hasLocation) {
      console.log('📍 Location selected:', this.deliveryLocationSelect.value);
    }
  }

  // ==========================================================================
  // PENDING CART STATE MANAGEMENT (Phase 3)
  // ==========================================================================

  /**
   * Get current product info from the page
   */
  getCurrentProductInfo() {
    const priceEl = document.querySelector('.price-item--sale') || document.querySelector('.price-item--regular');
    const price = priceEl ? parseFloat(priceEl.textContent.replace(/[^\d.]/g, '') || 0) : 0;
    
    // Get calorie plan from variant dropdown (first option)
    const calorieSelect = document.querySelector(`#Option-${this.config.sectionId}-0`);
    const caloriePlan = calorieSelect ? calorieSelect.value : '';
    
    // Get meal selection from variant dropdown (second option)
    const mealSelect = document.querySelector(`#Option-${this.config.sectionId}-1`);
    const mealSelection = mealSelect ? mealSelect.value : '';
    
    // Get the CURRENT variant ID from the product form (updated by Shopify when variant changes)
    // This is critical - we need the currently selected variant, not the initial page load variant
    const variantId = this.getCurrentVariantId();
    
    return {
      title: this.config.productTitle,
      price: price,
      priceInCents: Math.round(price * 100),
      variantId: variantId,
      caloriePlan: caloriePlan,
      mealSelection: mealSelection
    };
  }

  /**
   * Get the currently selected variant ID
   * Priority: 1) URL parameter, 2) Calorie dropdown, 3) Product form hidden input, 4) data-selected-variant JSON, 5) config fallback
   */
  getCurrentVariantId() {
    // Method 1: Get from URL parameter (most reliable when variant changes)
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');
    if (urlVariant) {
      const variantId = parseInt(urlVariant, 10);
      if (!isNaN(variantId)) {
        console.log('✅ Got variant ID from URL:', variantId);
        return variantId;
      }
    }
    
    // Method 2: Get from the original calorie dropdown's selected option productUrl
    const variantSelects = document.querySelector(`#variant-selects-${this.config.sectionId}`);
    if (variantSelects) {
      const calorieDropdown = variantSelects.querySelector('select[id^="Option-"]');
      if (calorieDropdown && calorieDropdown.selectedOptions[0]) {
        const productUrl = calorieDropdown.selectedOptions[0].dataset.productUrl;
        if (productUrl) {
          const match = productUrl.match(/variant=(\d+)/);
          if (match) {
            const variantId = parseInt(match[1], 10);
            console.log('✅ Got variant ID from dropdown productUrl:', variantId);
            return variantId;
          }
        }
      }
    }
    
    // Method 3: Get from product form's hidden input
    const variantInput = document.querySelector(`#product-form-${this.config.sectionId} input[name="id"]`);
    if (variantInput && variantInput.value) {
      const variantId = parseInt(variantInput.value, 10);
      if (!isNaN(variantId)) {
        console.log('✅ Got variant ID from hidden input:', variantId);
        return variantId;
      }
    }
    
    // Method 4: Get from variant-selects data-selected-variant JSON
    const variantJson = document.querySelector(`#variant-selects-${this.config.sectionId} [data-selected-variant]`);
    if (variantJson) {
      try {
        const variant = JSON.parse(variantJson.textContent);
        if (variant && variant.id) {
          console.log('✅ Got variant ID from JSON:', variant.id);
          return variant.id;
        }
      } catch (e) {
        console.warn('Failed to parse variant JSON:', e);
      }
    }
    
    // Method 5: Fallback to initial config (should rarely happen)
    console.warn('⚠️ Could not get current variant ID, using config fallback');
    return this.config.variantId;
  }

  /**
   * Get current delivery location info
   */
  getCurrentLocationInfo() {
    if (!this.deliveryLocationSelect || !this.deliveryLocationSelect.value) {
      return null;
    }
    
    const option = this.deliveryLocationSelect.options[this.deliveryLocationSelect.selectedIndex];
    return {
      slug: this.deliveryLocationSelect.value,
      name: option.dataset.locationName || this.deliveryLocationSelect.value,
      priceInCents: parseInt(option.dataset.price || 0),
      price: parseInt(option.dataset.price || 0) / 100,
      variantId: option.dataset.variantId || null
    };
  }

  /**
   * Recalculate pending cart state whenever inputs change
   * This pre-builds everything before Add to Cart is clicked
   */
  recalculatePendingCart() {
    const quantity = parseInt(this.quantityInput.value) || 1;
    const product = this.getCurrentProductInfo();
    const location = this.getCurrentLocationInfo();
    const sortedDates = this.selectedDates.slice().sort((a, b) => a - b);
    
    // Reset state
    this.pendingCart = {
      items: [],
      mealSubtotal: 0,
      shippingTotal: 0,
      grandTotal: 0,
      dateCount: sortedDates.length,
      quantity: quantity
    };
    
    if (!location || sortedDates.length === 0) {
      return;
    }
    
    // Build items for each date
    sortedDates.forEach(date => {
      const formattedDate = flatpickr.formatDate(date, "F j, Y");
      const shippingFeeFormatted = formatPrice(location.price);
      
      // Meal item with enhanced metadata
      const purchaseReason = this.purchaseReasonSelect ? this.purchaseReasonSelect.value : '';
      this.pendingCart.items.push({
        type: 'meal',
        variantId: product.variantId,
        quantity: quantity,
        properties: {
          "Selected Consumption Dates": formattedDate,
          "Delivery Location": location.slug,
          "Delivery Location Name": location.name,
          "Calorie Plan": product.caloriePlan,
          "Shipping Fee": shippingFeeFormatted,
          ...(purchaseReason ? { "Purchase Reason": purchaseReason } : {})
        },
        // Internal tracking
        _date: date,
        _price: product.price * quantity
      });
      
      // Shipping item with enhanced metadata
      const deliveryPeriodSelect = document.querySelector(`#Option-${this.config.sectionId}-2`);
      const deliveryPeriodValue = deliveryPeriodSelect ? deliveryPeriodSelect.value : '';
      
      if (location.variantId) {
        this.pendingCart.items.push({
          type: 'shipping',
          variantId: location.variantId,
          quantity: 1,
          properties: {
            _shipfee: "true",
            "Delivery Date": formattedDate,
            "Delivery Location": location.slug,
            "Delivery Location Name": location.name,
            "Delivery Period": deliveryPeriodValue,
            "Linked Product": product.title,
            "Calorie Plan": product.caloriePlan,
            "Meal Selection": product.mealSelection
          },
          // Internal tracking
          _date: date,
          _price: location.price
        });
      }
    });
    
    // Calculate totals
    this.pendingCart.mealSubtotal = product.price * quantity * sortedDates.length;
    this.pendingCart.shippingTotal = location.price * sortedDates.length;
    this.pendingCart.grandTotal = this.pendingCart.mealSubtotal + this.pendingCart.shippingTotal;
    
    console.log('📊 Pending cart recalculated:', {
      variantId: product.variantId,
      caloriePlan: product.caloriePlan,
      items: this.pendingCart.items.length,
      dates: sortedDates.length,
      mealSubtotal: this.pendingCart.mealSubtotal,
      shippingTotal: this.pendingCart.shippingTotal,
      grandTotal: this.pendingCart.grandTotal
    });
  }

  /**
   * Build the payload for Shopify Cart API
   * Returns an array of items ready for /cart/add.js
   */
  buildCartPayload() {
    return this.pendingCart.items.map(item => ({
      id: item.variantId,
      quantity: item.quantity,
      properties: item.properties
    }));
  }

  // ==========================================================================
  // UI UPDATE
  // ==========================================================================

  updateUI() {
    this.updatePriceSummary();
    this.updateButtons();
  }

  updatePriceSummary() {
    const product = this.getCurrentProductInfo();
    const location = this.getCurrentLocationInfo();
    const dateCount = this.selectedDates.length;
    const quantity = parseInt(this.quantityInput.value) || 1;
    
    // Update individual elements
    const updates = {
      'productPrice': formatPrice(product.price),
      'totalQty': quantity,
      'daysNumber': dateCount,
      'shipping-fee-per-day': location ? location.price.toFixed(2) : '0.00',
      'shipping-days-count': dateCount,
      'shipping-fee': formatPrice(this.pendingCart.shippingTotal),
      'total-amount': formatPrice(this.pendingCart.grandTotal)
    };
    
    Object.entries(updates).forEach(([id, value]) => {
      const el = document.getElementById(id);
      if (el) el.textContent = value;
    });
  }

  updateButtons() {
    const hasValidOrder = this.hasValidOrder();
    const checkoutEnabled = hasValidOrder || this.addedToCartThisSession;
    const shouldDisableCheckout = !checkoutEnabled || this.isSubmitting;

    console.log('🔘 updateButtons:', {
      hasValidOrder,
      addedToCartThisSession: this.addedToCartThisSession,
      checkoutEnabled,
      isSubmitting: this.isSubmitting,
      shouldDisableCheckout,
      buyNowBtnExists: !!this.buyNowBtn
    });

    // Add to Cart button: only enabled with a valid new order
    this.addToCartBtn.disabled = !hasValidOrder || this.isSubmitting;

    // Checkout Now: enabled with a valid order OR if already added to cart this session
    this.setCheckoutDisabled(shouldDisableCheckout);
  }

  hasValidOrder() {
    const hasLocation = this.deliveryLocationSelect && this.deliveryLocationSelect.value;
    const hasReason = !this.purchaseReasonSelect || !!this.purchaseReasonSelect.value;
    const qty = parseInt(this.quantityInput.value, 10) || 0;
    // Block ordering when the period override/location intersection is empty,
    // or when today is hard-blocked.
    const periodBlocked = !!this._periodOverrideIntersectionEmpty;
    const todayBlocked  = !!this._todayIsBlocked;
    const result = hasLocation && hasReason && this.selectedDates.length > 0 && qty > 0 && !periodBlocked && !todayBlocked;

    console.log('✓ hasValidOrder:', {
      hasLocation,
      hasReason,
      selectedDatesCount: this.selectedDates.length,
      qty,
      periodBlocked,
      todayBlocked,
      result
    });

    return result;
  }

  setCheckoutDisabled(disabled) {
    if (!this.buyNowBtn) {
      console.log('⚠️ setCheckoutDisabled: buyNowBtn not found');
      return;
    }

    console.log('🔘 setCheckoutDisabled:', { disabled, currentHref: this.buyNowBtn.getAttribute('href'), hasIsDisabled: this.buyNowBtn.classList.contains('is-disabled') });

    this.buyNowBtn.classList.toggle('is-disabled', disabled);
    this.buyNowBtn.setAttribute('aria-disabled', String(disabled));

    if (this.buyNowBtn.tagName === 'A') {
      if (disabled) {
        if (!this.buyNowBtn.dataset.href) {
          this.buyNowBtn.dataset.href = this.buyNowBtn.getAttribute('href') || '';
        }
        this.buyNowBtn.setAttribute('href', '#');
        this.buyNowBtn.setAttribute('tabindex', '-1');
      } else {
        if (this.buyNowBtn.dataset.href !== undefined) {
          this.buyNowBtn.setAttribute('href', this.buyNowBtn.dataset.href);
          delete this.buyNowBtn.dataset.href;
        }
        this.buyNowBtn.removeAttribute('tabindex');
      }
    } else {
      this.buyNowBtn.disabled = disabled;
    }
  }

  // ==========================================================================
  // ADD TO CART (Phase 4)
  // ==========================================================================

  showAllergenAcknowledgmentDialog() {
    if (this.isAllergenPromptOpen || activeAllergenPrompt) return Promise.resolve(false);
    this.isAllergenPromptOpen = true;

    const copy = {
      eyebrow: 'Required before purchase',
      title: 'Allergen advisory',
      summary: 'Our meals are prepared in a shared commercial kitchen where peanuts, tree nuts, milk, eggs, soy, wheat (gluten), fish, shellfish, and sesame are handled daily. We cannot guarantee that any meal is free from allergen cross-contact or provide allergen-free preparation.',
      link: 'Read the full allergen declaration',
      checkbox: 'I have read and understood the Allergen Advisory and agree to the Acknowledgment, Waiver and Assumption of Risk. I understand that these meals are prepared in a shared kitchen, may be exposed to allergen cross-contact, and are not suitable for individuals who require allergen-free preparation. I accept the associated risks in proceeding with my order.',
      cancel: 'Go back',
      confirm: 'I understand and agree',
      close: 'Close allergen advisory',
      ...(this.config.allergen || {})
    };
    const allergenUrl = this.config.allergenUrl || '/pages/allergen-declaration';
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const modal = document.createElement('div');
    modal.className = 'allergen-prompt';
    modal.innerHTML = `
      <button type="button" class="allergen-prompt__backdrop" data-allergen-close aria-label="${escapeHtml(copy.close)}" tabindex="-1"></button>
      <section class="allergen-prompt__dialog" role="dialog" aria-modal="true" aria-labelledby="allergen-prompt-title" aria-describedby="allergen-prompt-summary">
        <div class="allergen-prompt__header">
          <div>
            <p>${escapeHtml(copy.eyebrow)}</p>
            <h2 id="allergen-prompt-title">${escapeHtml(copy.title)}</h2>
          </div>
          <button type="button" class="allergen-prompt__close" data-allergen-close aria-label="${escapeHtml(copy.close)}">×</button>
        </div>
        <div class="allergen-prompt__summary" id="allergen-prompt-summary">
          <span class="allergen-prompt__warning" aria-hidden="true">!</span>
          <div>
            <p>${escapeHtml(copy.summary)}</p>
            <a href="${escapeHtml(allergenUrl)}" target="_blank" rel="noreferrer">${escapeHtml(copy.link)} <span aria-hidden="true">→</span></a>
          </div>
        </div>
        <label class="allergen-prompt__consent">
          <input type="checkbox">
          <span class="allergen-prompt__check" aria-hidden="true"></span>
          <span>${escapeHtml(copy.checkbox)}</span>
        </label>
        <div class="allergen-prompt__actions">
          <button type="button" class="allergen-prompt__cancel" data-allergen-close>${escapeHtml(copy.cancel)}</button>
          <button type="button" class="allergen-prompt__confirm" disabled>${escapeHtml(copy.confirm)} <span aria-hidden="true">→</span></button>
        </div>
      </section>
    `;

    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    const dialog = modal.querySelector('.allergen-prompt__dialog');
    const checkbox = modal.querySelector('.allergen-prompt__consent input');
    const checkmark = modal.querySelector('.allergen-prompt__check');
    const confirmButton = modal.querySelector('.allergen-prompt__confirm');
    const closeButton = modal.querySelector('.allergen-prompt__close');

    return new Promise(resolve => {
      let finished = false;
      const finish = accepted => {
        if (finished) return;
        finished = true;
        activeAllergenPrompt = null;
        this.cancelAllergenPrompt = null;
        window.removeEventListener('keydown', handleKeydown);
        document.body.style.overflow = previousOverflow;
        modal.remove();
        this.isAllergenPromptOpen = false;
        if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
        resolve(accepted);
      };

      const handleKeydown = event => {
        if (event.key === 'Escape') {
          finish(false);
          return;
        }
        if (event.key !== 'Tab') return;

        const focusable = Array.from(dialog.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        ));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      checkbox.addEventListener('change', () => {
        confirmButton.disabled = !checkbox.checked;
        checkmark.textContent = checkbox.checked ? '✓' : '';
      });
      confirmButton.addEventListener('click', () => { if (checkbox.checked) finish(true); });
      modal.querySelectorAll('[data-allergen-close]').forEach(element => {
        element.addEventListener('click', () => finish(false));
      });
      activeAllergenPrompt = finish;
      this.cancelAllergenPrompt = () => finish(false);
      window.addEventListener('keydown', handleKeydown);
      closeButton.focus();
    });
  }

  allergenProperties(acknowledgedAt) {
    return {
      [ALLERGEN_ACKNOWLEDGMENT_KEY]: 'Yes',
      [ALLERGEN_DECLARATION_VERSION_KEY]: ALLERGEN_DECLARATION_VERSION,
      [ALLERGEN_ACKNOWLEDGED_AT_KEY]: acknowledgedAt
    };
  }

  /**
   * Unified Add to Cart handler
   * @param {boolean} redirectToCheckout - If true, redirect to checkout after adding
   */
  async handleAddToCart(redirectToCheckout = false) {
    if (!this.hasValidOrder() || this.isSubmitting || this.isAllergenPromptOpen) return;

    const allergenAccepted = await this.showAllergenAcknowledgmentDialog();
    if (!allergenAccepted) return;
    const allergenProperties = this.allergenProperties(new Date().toISOString());
    
    this.isSubmitting = true;
    const originalText = this.addToCartBtn.textContent;
    const originalCheckoutText = redirectToCheckout && this.buyNowBtn ? this.buyNowBtn.textContent : null;
    
    try {
      // Phase 1: Preparing (haptic feedback)
      this.setButtonState('preparing');
      if (redirectToCheckout && this.buyNowBtn) this.setCheckoutButtonText('Adding to Cart...');
      triggerHaptic('preparing');
      
      // Ensure pending cart is up to date
      this.recalculatePendingCart();
      
      if (this.pendingCart.items.length === 0) {
        throw new Error('No items to add');
      }
      
      // Fetch current cart to check for existing items
      const cart = await this.fetchCurrentCart();
      const currentLocation = this.getCurrentLocationInfo();
      const cartLocation = this.getCartDeliveryLocation(cart);
      
      // Check for location mismatch (Phase 5 - to be implemented with proper UI)
      if (cartLocation && cartLocation.slug !== currentLocation.slug) {
        const userChoice = await this.showLocationMismatchDialog(cartLocation, currentLocation);
        
        if (userChoice === 'cancel') {
          this.isSubmitting = false;
          this.setButtonState('default', originalText);
          if (originalCheckoutText) this.setCheckoutButtonText(originalCheckoutText);
          return;
        } else if (userChoice === 'keep-existing') {
          // Update product page to match cart location
          this.deliveryLocationSelect.value = cartLocation.slug;
          this.handleLocationChange();
          this.recalculatePendingCart();
        } else if (userChoice === 'change-all') {
          // Update all cart items to new location
          await this.updateCartToNewLocation(cart, currentLocation);
        }
      }
      
      // Re-fetch cart after potential location change
      const updatedCart = cartLocation && cartLocation.slug !== currentLocation.slug 
        ? await this.fetchCurrentCart() 
        : cart;
      
      // Get existing shipping dates to avoid duplicates
      const finalLocation = this.getCurrentLocationInfo();
      const existingShippingDates = this.getExistingShippingDates(updatedCart, finalLocation.slug);
      console.log(`📍 Existing shipping dates in cart: ${existingShippingDates.size}`);
      
      // Phase 2: Submitting
      this.setButtonState('submitting');
      triggerHaptic('medium');
      
      // Build payload, filtering out shipping for dates that already have it
      const payload = this.pendingCart.items
        .filter(item => {
          if (item.type === 'shipping') {
            const dateStr = item.properties['Delivery Date'];
            if (existingShippingDates.has(dateStr)) {
              console.log(`⏭️ Skipping duplicate shipping for ${dateStr}`);
              return false;
            }
          }
          return true;
        })
        .map(item => ({
          id: item.variantId,
          quantity: item.quantity,
          properties: {
            ...item.properties,
            ...(item.type === 'meal' ? allergenProperties : {})
          }
        }));
      
      if (payload.length === 0) {
        throw new Error('No new items to add');
      }
      
      console.log(`📦 Submitting ${payload.length} items (skipped ${this.pendingCart.items.length - payload.length} duplicate shipping)`);
      
      // Submit ALL items in ONE request using Shopify's items array format
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payload })
      });
      
      let successCount = 0;
      
      if (response.ok) {
        const result = await response.json();
        successCount = result.items ? result.items.length : payload.length;
        console.log(`✅ Bulk add successful:`, result);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Bulk add failed:', errorData);
        
        // If bulk fails, try sequential as fallback
        console.log('⚠️ Falling back to sequential add...');
        for (const item of payload) {
          try {
            const itemResponse = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(item)
            });
            
            if (itemResponse.ok) {
              successCount++;
            }
            await new Promise(resolve => setTimeout(resolve, 30));
          } catch (err) {
            console.error('❌ Sequential add error:', err);
          }
        }
      }
      
      console.log(`📦 Added ${successCount} items to cart`);
      
      if (successCount > 0) {
        // Success!
        this.addedToCartThisSession = true;
        triggerHaptic('success');
        
        // Refresh cart state
        await this.refreshCartState();
        
        // Track with Facebook Pixel
        this.trackAddToCart();
        
        // Show success message
        showToast('Meal(s) successfully added to cart!');
        
        // Clear selected dates to prevent duplicate adds
        this.selectedDates = [];
        if (this.flatpickrInstance) {
          this.flatpickrInstance.clear();
        }
        this.recalculatePendingCart();
        
        // Update UI
        this.updateUI();
        
        // Redirect to cart page if Checkout Now
        if (redirectToCheckout) {
          window.location.href = '/cart';
        }
      } else {
        throw new Error('Failed to add items to cart');
      }
      
    } catch (error) {
      console.error('❌ Add to cart error:', error);
      triggerHaptic('error');
      showToast('Error adding to cart. Please try again.', 'error');
    } finally {
      this.isSubmitting = false;
      this.setButtonState('default', originalText);
      if (originalCheckoutText && this.buyNowBtn) this.setCheckoutButtonText(originalCheckoutText);
      this.updateButtons();
    }
  }

  /**
   * Set Checkout Now button text (for loading feedback when user clicked it)
   */
  setCheckoutButtonText(text) {
    if (!this.buyNowBtn) return;
    this.buyNowBtn.textContent = text;
  }

  /**
   * Set button visual state
   */
  setButtonState(state, customText = null) {
    const states = {
      preparing: { text: 'Preparing...', disabled: true },
      submitting: { text: 'Adding to Cart...', disabled: true },
      default: { text: customText || 'Add to Cart', disabled: !this.hasValidOrder() }
    };
    
    const config = states[state] || states.default;
    this.addToCartBtn.textContent = config.text;
    this.addToCartBtn.disabled = config.disabled;
  }

  /**
   * Track Add to Cart event with Facebook Pixel
   */
  trackAddToCart() {
    if (typeof fbq === 'undefined') return;
    
    const totalValue = this.pendingCart.grandTotal;
    
    fbq('track', 'AddToCart', {
      value: totalValue,
      currency: 'PHP',
      content_type: 'product',
      content_ids: [this.config.productId],
      num_items: this.pendingCart.dateCount
    }, {
      eventID: 'add_' + Date.now()
    });
    
    console.log('🧠 fbq AddToCart sent:', totalValue, 'PHP');
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

document.addEventListener("DOMContentLoaded", () => {
  if (document.querySelector("[data-holiday-edit]")) return;
  if (typeof window.NumMealsConfig === 'undefined') {
    console.warn('NumMealsConfig not found. Product cart manager will not initialize.');
    return;
  }
  
  window.productCartManager = new ProductCartManager(window.NumMealsConfig);
});

// Holiday ordering has its own state and Storefront cart. It never calls the
// regular manager's Ajax mutation, reconstruction, or checkout paths.
(() => {
  'use strict';
  const STORAGE_KEY = 'nummeals.holiday-2026.selection.v1';
  // Immutable capabilities: catalogue browsing and unpublished review never gain purchasing rights.
  const readOnlyControllers = new WeakSet();
  const catalogueControllers = new WeakSet();
  const readOnlyAdapters = new WeakSet();
  const WEEK = 7 * 24 * 60 * 60 * 1000;
  const ORDERING_LAST_DATE = '2026-12-20';
  const HOLIDAY_TERMS_VERSION = '2026-11-04-r16';
  const HOLIDAY_DELIVERY_WINDOW = '12:00 PM–6:00 PM';
  const PERIODS = { 'SAME DAY DELIVERY': 'AM', 'DAY BEFORE CONSUMPTION': 'PM' };
  const PAIRS = { 'party-tray': ['beef', 'pork', 'chicken', 'pasta-noodles'], 'packed-meal': ['beef', 'pork', 'chicken'], rice: ['rice'] };
  const privateCarts = new WeakMap();
  const instances = new WeakMap();
  const fallback = {
    addTray: 'Add tray', addRice: 'Add rice', addPacked: 'Add 20-pack set',
    increase: 'Increase {dish} {unit}', decrease: 'Decrease {dish} {unit}',
    trayOne: 'tray', trayMany: 'trays', setOne: 'set', setMany: 'sets',
    packSummary: '{sets} · {packs} packs', added: '{dish}: {quantity} {unit} in your holiday bag.',
    removed: '{dish} removed from your holiday bag.', bagName: 'Holiday bag, {count} items', bagNameOne: 'Holiday bag, {count} item',
    groupsParty: 'Party trays', groupsPacked: 'Packed meals', groupsRice: 'Rice', noDates: 'No delivery dates are available for this area. Try another area or return later.',
    browse: 'Browse the menu', reviewConsent: 'Review allergen declaration', chooseDelivery: 'Choose delivery', deliveryPrompt: 'Choose an area and delivery date.', checkingDelivery: 'Checking delivery…', checkoutUnavailable: 'Checkout unavailable', chooseWindowFirst: 'Choose a time window first.', unavailableSelection: 'Some items are unavailable for this delivery. Remove them from your order or choose another delivery date.', reviewChanges: 'Review changes', checking: 'Checking your order…',
    candidateNotice: 'Confirm your saved delivery. We will check current availability and fees.', expiredCandidate: 'Your saved delivery expired. Choose a current delivery date.',
    chooseFee: 'Choose delivery to see the fee', leadTime: 'Choose an available delivery date at least two calendar days from today.',
    otherMonth: 'Use the month controls to choose this date', pastDate: 'Choose a delivery date at least two calendar days from today.', weekendDate: 'Delivery is available Monday to Friday.', endedWindow: 'This delivery date is unavailable. Choose another date.', noAreas: 'No delivery areas are available. Please try again later.', noDatesMonth: 'No available dates this month. Try another month or area.', closedDate: 'No delivery on this date', chooseAreaFirst: 'Choose a delivery area first', chooseDateFirst: 'Choose a delivery date first.',
    partyOne: 'party tray', partyMany: 'party trays', riceOne: 'rice tray', riceMany: 'rice trays', packedUnit: '20-pack set',
    bagSummary: '{items} · {trays} + {sets} ({packs} packs)',
    empty: 'Your holiday bag is empty.', unavailable: 'Unavailable for this delivery date.',
    limit: 'This quantity is not available. Please choose a supported quantity.',
    configuration: 'Holiday ordering is not available yet. Your selection is kept.',
    catalogueBlocked: 'Checkout is not available yet. Your holiday bag is saved on this device.',
    viewCart: 'View Cart', estimatedSubtotal: 'Estimated subtotal', deliveryExtra: 'Delivery fee not included', deliveryIncluded: 'Delivery fee included',
    operationalError: 'Delivery availability could not be loaded. Please retry.',
    orderingClosed: 'Holiday Edit 2026 ordering closed on December 20, 2026.',
    termsRequired: 'Please read and accept the Holiday Edit 2026 Terms & Conditions before checkout.',
    deliveryWindow: HOLIDAY_DELIVERY_WINDOW,
    deliveryRequired: 'Choose your delivery area and date.', invalidDate: 'Choose an available delivery date.',
    unavailableWindow: 'This delivery date is unavailable for your selection.',
    confirmSlot: 'Update the delivery for every item in your holiday bag?',
    removeUnavailable: 'Some dishes are unavailable for this delivery. Remove those dishes and update delivery?',
    deliveryUpdated: 'Delivery updated to {area}, {date}.', updating: 'Updating…',
    unknownCreate: 'The attempt was interrupted. Your selection is kept. Retry to create a new holiday cart.',
    cartExpired: 'Your holiday cart could not be read. Review your selection and accept the declaration again.',
    cartChanged: 'The holiday cart has changed. Review your selection and retry.',
    priceChanged: 'Prices have changed. Review the updated total and confirm again.',
    feeMismatch: 'The delivery fee could not be verified. Please retry later.',
    inventory: 'Some quantities are no longer available. Review your selection.',
    network: 'The request could not be completed. Your selection is kept. Please retry.',
    checkoutBlocked: 'Review your latest changes before checkout.', consentRequired: 'Accept the allergen declaration to continue.',
    committed: 'Confirmed holiday selection', pending: 'Holiday selection', subtotal: 'Subtotal', delivery: 'Delivery', total: 'Total',
    checkout: 'Continue to checkout', close: 'Close', editDelivery: 'Edit delivery', itemOne: 'item', itemMany: 'items', perPack: '{price} per pack', am: '5–9 AM', pm: '3–9 PM', confirmedQuantity: 'Confirmed: {quantity}. Requested: {requested}.'
  };
  function fail(code, details) { const error = new Error(code); error.code = code; if (details) error.details = details; return error; }
  function arraySetting(value) {
    try { const parsed = typeof value === 'string' ? JSON.parse(value) : value; return Array.isArray(parsed) ? parsed : null; } catch (_) { return null; }
  }
  function gid(value) {
    const text = String(value || '');
    if (/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(text)) return text;
    return /^\d+$/.test(text) ? `gid://shopify/ProductVariant/${text}` : null;
  }
  function moneyToCents(money) {
    if (!money || money.currencyCode !== 'PHP' || !/^\d+(?:\.\d{1,2})?$/.test(String(money.amount))) throw fail('currency');
    const [whole, fraction = ''] = String(money.amount).split('.');
    const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
    if (!Number.isSafeInteger(cents)) throw fail('currency');
    return cents;
  }
  function ruleValid(rule) {
    return !!rule && Number.isSafeInteger(rule.minimum) && rule.minimum > 0 && Number.isSafeInteger(rule.increment) && rule.increment > 0 &&
      (rule.maximum === null || (Number.isSafeInteger(rule.maximum) && rule.maximum >= rule.minimum));
  }
  function validQuantity(quantity, rule) {
    if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > 2147483647) return false;
    if (quantity === 0) return true;
    if (!rule) return true;
    return ruleValid(rule) && quantity >= rule.minimum && (quantity - rule.minimum) % rule.increment === 0 && (rule.maximum === null || quantity <= rule.maximum);
  }
  function normalizeCatalog(products) {
    const dishes = [], errors = [], keys = new Set(), ids = new Set(), duplicateIds = new Set();
    for (const product of Array.isArray(products) ? products : []) {
      if (!product || product.published !== true) continue;
      const tags = Array.isArray(product.tags) ? product.tags : [];
      if (tags.filter(tag => typeof tag === 'string' && tag.startsWith('nm:campaign:')).length !== 1 || !tags.includes('nm:campaign:holiday-2026')) { errors.push('campaign'); continue; }
      const formats = tags.filter(tag => typeof tag === 'string' && tag.startsWith('nm:format:'));
      const categories = tags.filter(tag => typeof tag === 'string' && tag.startsWith('nm:category:'));
      const format = (formats[0] || '').slice(10), category = (categories[0] || '').slice(12);
      if (formats.length !== 1 || categories.length !== 1 || !PAIRS[format]?.includes(category)) { errors.push('classification'); continue; }
      const groups = new Map();
      for (const variant of Array.isArray(product.variants) ? product.variants : []) {
        if (!variant || typeof variant.dishKey !== 'string' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(variant.dishKey)) { errors.push('dishKey'); continue; }
        const group = groups.get(variant.dishKey) || [];
        group.push(variant); groups.set(variant.dishKey, group);
      }
      for (const [dishKey, group] of groups) {
        const first = group[0], key = `${format}:${dishKey}`;
        let invalid = group.length !== 2 || keys.has(key) || (typeof first.dishName !== 'string' || !first.dishName.trim()) || typeof first.image !== 'string' || !Number.isSafeInteger(first.priceCents) || first.priceCents < 0;
        const variants = {};
        for (const variant of group) {
          const window = PERIODS[variant.period], id = gid(variant.id);
          if (!window || variants[window] || !id || ids.has(id) || variant.dishName !== first.dishName || variant.image !== first.image || variant.priceCents !== first.priceCents) invalid = true;
          if (id && ids.has(id)) duplicateIds.add(id);
          if (id) ids.add(id);
          if (window) variants[window] = { id, available: variant.available === true, priceCents: variant.priceCents };
        }
        if (!variants.AM || !variants.PM) invalid = true;
        if (keys.has(key)) { const previous = dishes.find(dish => dish.key === key); if (previous) previous.error = 'metadata'; }
        keys.add(key);
        dishes.push({ key, dishKey, name: first.dishName || dishKey, format, category, image: first.image, priceCents: Number.isSafeInteger(first.priceCents) && first.priceCents >= 0 ? first.priceCents : 0, variants, error: invalid ? 'metadata' : null });
        if (invalid) errors.push(`metadata:${key}`);
      }
    }
    for (const dish of dishes) if (Object.values(dish.variants).some(variant => duplicateIds.has(variant.id))) dish.error = 'metadata';
    return { dishes, errors };
  }
  function parseISODate(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(`${value}T12:00:00.000Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? value : null;
  }
  function addDays(value, days) {
    if (!parseISODate(value) || !Number.isSafeInteger(days)) throw fail('invalidDate');
    const date = new Date(`${value}T12:00:00.000Z`); date.setUTCDate(date.getUTCDate() + days); return date.toISOString().slice(0, 10);
  }
  function manilaToday(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const value = type => parts.find(part => part.type === type).value;
    return `${value('year')}-${value('month')}-${value('day')}`;
  }
  function consumptionDate(date, window) { if (!['AM', 'PM'].includes(window)) throw fail('unavailableWindow'); return addDays(date, window === 'PM' ? 1 : 0); }
  function canonicalDate(date) { return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`)); }
  function validOrigin(value) { try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password && url.origin === value; } catch (_) { return false; } }
  function validateCheckoutUrl(value, origins) {
    const allowed = arraySetting(origins);
    try { const url = new URL(value); if (!allowed?.length || !allowed.every(validOrigin) || url.protocol !== 'https:' || url.username || url.password || !allowed.includes(url.origin)) throw fail('checkoutBlocked'); return url.href; } catch (_) { throw fail('checkoutBlocked'); }
  }
  function isPreviewConfig(config) { return config?.preview?.enabled === true && ['unpublished', 'development'].includes(config.preview.themeRole) && String(config.preview.themeId) === '187620360405' && config?.campaign?.ready === false && config?.catalogueOnly !== true; }
  function isCatalogueConfig(config) { return config?.catalogueOnly === true && config?.preview?.enabled === false && config?.campaign?.ready === false; }
  function validateConfig(config) {
    const errors = [], c = config?.campaign || {}, o = config?.operations || {}, s = config?.storefront || {};
    const closures = arraySetting(c.closures), origins = arraySetting(s.checkoutOrigins);
    if (config?.version !== 1 || config?.currency !== 'PHP' || (config?.catalogueOnly === true ? !isCatalogueConfig(config) : config?.preview?.enabled === true ? !isPreviewConfig(config) : c.ready !== true)) errors.push('configuration');
    if (!closures || closures.some(date => !parseISODate(date))) errors.push('invalidDate');
    if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(s.shopDomain || '') || (!isPreviewConfig(config) && !isCatalogueConfig(config) && (!s.publicToken?.trim() || !origins?.length || !origins.every(validOrigin)))) errors.push('configuration');
    if (!['D', 'C'].includes(o.blocksBasis) || !['D', 'C'].includes(o.overridesBasis) || !o.publicKey || !validOrigin(o.supabaseUrl)) errors.push('configuration');
    if (config?.allergen?.version !== ALLERGEN_DECLARATION_VERSION) errors.push('consentRequired');
    return [...new Set(errors)];
  }
  function validateOperations(operations) {
    if (!operations || !Array.isArray(operations.locations) || !Array.isArray(operations.blocks) || !Array.isArray(operations.overrides)) throw fail('operationalError');
    const slugs = new Set();
    for (const location of operations.locations) {
      if (!location.location_slug || !location.location_name || slugs.has(location.location_slug) || !['both', 'same_day', 'day_before'].includes(location.delivery_timing_capability) || !Number.isSafeInteger(location.price_in_cents) || location.price_in_cents < 0 || !gid(location.shopify_variant_id) || location.is_active !== true) throw fail('operationalError');
      slugs.add(location.location_slug);
    }
    if (operations.blocks.some(row => !parseISODate(row.blocked_date)) || operations.overrides.some(row => !parseISODate(row.target_date) || !['both', 'same_day', 'day_before'].includes(row.allowed_timing))) throw fail('operationalError');
    const dates = operations.overrides.map(row => row.target_date);
    if (new Set(dates).size !== dates.length) throw fail('operationalError');
    return operations;
  }
  function dateEligibility(date, window = null, now = new Date()) {
    if (!parseISODate(date)) return 'invalidDate';
    const today = manilaToday(now);
    if (today > ORDERING_LAST_DATE) return 'orderingClosed';
    if (date < today) return 'pastDate';
    if (date < addDays(today, 2)) return 'leadTime';
    return null;
  }
  function firstAvailableDeliveryDate(config, operations, now = new Date(), selectedLocation = '', selection = {}, dishes = []) {
    if (validateConfig(config).length) return { date: null, error: 'configuration' };
    if (manilaToday(now) > ORDERING_LAST_DATE) return { date: null, error: 'orderingClosed' };
    try { validateOperations(operations); } catch (_) { return { date: null, error: 'operationalError' }; }
    const locations = operations.locations.filter(location => !selectedLocation || location.location_slug === selectedLocation);
    if (!locations.length) return { date: null, error: 'noAreas' };
    // Dated exceptions are finite. A C-based exception can affect D=C (AM)
    // or D=C-1 (PM); no recurring closure or business end date is inferred.
    const exceptions = new Set(arraySetting(config.campaign.closures));
    const addException = (date, basis) => { exceptions.add(date); if (basis === 'C') exceptions.add(addDays(date, -1)); };
    operations.blocks.forEach(row => addException(row.blocked_date, config.operations.blocksBasis));
    operations.overrides.forEach(row => addException(row.target_date, config.operations.overridesBasis));
    // Search begins two calendar days ahead; dated closures and operational
    // exceptions can move the first compatible date later, including weekends.
    const limit = exceptions.size + 2;
    let date = addDays(manilaToday(now), 2), examined = 0;
    while (examined < limit && parseISODate(date)) {
      examined++;
      if (locations.some(location => resolveDelivery({ location: location.location_slug, date }, config, operations, now, selection, dishes).valid)) return { date, error: null };
      date = addDays(date, 1);
    }
    return { date: null, error: 'unavailableWindow' };
  }
  function validateDelivery(slot, config, operations, now = new Date()) {
    const invalid = error => ({ valid: false, error });
    if (validateConfig(config).length) return invalid('configuration');
    try { validateOperations(operations); } catch (_) { return invalid('operationalError'); }
    if (!slot || !slot.location || !parseISODate(slot.date) || !['AM', 'PM'].includes(slot.window)) return invalid('deliveryRequired');
    const c = config.campaign, date = slot.date, canonical = consumptionDate(date, slot.window);
    if (!parseISODate(canonical)) return invalid('invalidDate');
    const dateError = dateEligibility(date, slot.window, now);
    if (dateError) return invalid(dateError);
    if (arraySetting(c.closures).includes(date)) return invalid('invalidDate');
    const location = operations.locations.find(row => row.location_slug === slot.location && row.is_active);
    if (!location) return invalid('deliveryRequired');
    const timing = slot.window === 'AM' ? 'same_day' : 'day_before';
    if (location.delivery_timing_capability !== 'both' && location.delivery_timing_capability !== timing) return invalid('unavailableWindow');
    const blockDate = config.operations.blocksBasis === 'D' ? date : canonical;
    if (operations.blocks.some(row => row.blocked_date === blockDate)) return invalid('invalidDate');
    const overrideDate = config.operations.overridesBasis === 'D' ? date : canonical;
    const override = operations.overrides.find(row => row.target_date === overrideDate);
    if (override && override.allowed_timing !== 'both' && override.allowed_timing !== timing) return invalid('unavailableWindow');
    return { valid: true, error: null, location, consumptionDate: canonical };
  }
  // Operational periods remain an internal variant contract, never a customer choice.
  // A single period must support the entire bag, with AM preferred deterministically.
  function resolveDelivery(slot, config, operations, now = new Date(), selection = {}, dishes = []) {
    let firstError = null, operationallyValid = false;
    for (const window of ['AM', 'PM']) {
      const candidate = { location: slot?.location || '', date: slot?.date || '', window };
      const validation = validateDelivery(candidate, config, operations, now);
      if (!validation.valid) { firstError ||= validation.error; continue; }
      operationallyValid = true;
      const compatible = Object.entries(selection).every(([key, quantity]) => {
        if (!quantity) return true;
        const dish = dishes.find(item => item.key === key), variant = dish?.variants[window];
        return dish && !dish.error && !dish.channelError && variant?.available && validQuantity(quantity, variant.quantityRule);
      });
      if (compatible) return { ...validation, slot: candidate };
    }
    return { valid: false, error: operationallyValid ? 'unavailableSelection' : firstError || 'deliveryRequired' };
  }
  function attributes(object) { return Object.entries(object).map(([key, value]) => ({ key, value: String(value) })); }
  function deriveLines(selection, dishes, slot, operations, consent) {
    const entries = Object.entries(selection).filter(([, quantity]) => quantity > 0);
    if (!entries.length) return [];
    const location = operations?.locations?.find(row => row.location_slug === slot?.location && row.is_active === true);
    if (!location || !gid(location.shopify_variant_id) || !Number.isSafeInteger(location.price_in_cents) || location.price_in_cents < 0) throw fail('feeMismatch');
    if (!consent?.version || !Number.isFinite(Date.parse(consent.at))) throw fail('consentRequired');
    if (consent.termsVersion !== HOLIDAY_TERMS_VERSION || !Number.isFinite(Date.parse(consent.termsAt))) throw fail('termsRequired');
    const c = canonicalDate(consumptionDate(slot.date, slot.window));
    const common = { 'Delivery Location': location.location_slug, 'Delivery Location Name': location.location_name, 'Holiday Delivery Date': slot.date, 'Holiday Delivery Window': HOLIDAY_DELIVERY_WINDOW, _holiday_variant_period: slot.window, _holiday_campaign: '2026' };
    const lines = entries.map(([key, quantity]) => {
      const dish = dishes.find(item => item.key === key), variant = dish?.variants[slot.window];
      if (!dish || dish.error || !variant?.available || !variant.id || !validQuantity(quantity, variant.quantityRule)) throw fail('inventory');
      return { merchandiseId: variant.id, quantity, attributes: attributes({ 'Selected Consumption Dates': c, ...common, 'Shipping Fee': formatPrice(location.price_in_cents / 100), _holiday_dish_key: dish.dishKey, _holiday_format: dish.format,
        [ALLERGEN_ACKNOWLEDGMENT_KEY]: 'Yes', [ALLERGEN_DECLARATION_VERSION_KEY]: consent.version, [ALLERGEN_ACKNOWLEDGED_AT_KEY]: consent.at, 'Holiday Terms Acknowledgment': 'Yes', 'Holiday Terms Version': consent.termsVersion, 'Holiday Terms Acknowledged At': consent.termsAt }) };
    });
    lines.push({ merchandiseId: gid(location.shopify_variant_id), quantity: 1, attributes: attributes({ _shipfee: 'true', 'Delivery Date': c, ...common, 'Delivery Period': slot.window }) });
    return lines;
  }
  function readSelection(storage, dishes, now = new Date()) {
    try {
      const raw = storage?.getItem(STORAGE_KEY); if (!raw) return {};
      const saved = JSON.parse(raw), time = Date.parse(saved.savedAt), current = now.getTime();
      if (saved.version !== 1 || !Number.isFinite(time) || time > current || current - time >= WEEK || !saved.selection || typeof saved.selection !== 'object' || Array.isArray(saved.selection)) return {};
      const selection = Object.fromEntries(Object.entries(saved.selection).filter(([key, quantity]) => dishes.some(dish => dish.key === key) && validQuantity(quantity) && quantity > 0 && Number.isSafeInteger(quantity * (dishes.find(dish => dish.key === key).priceCents || 1))));
      return Number.isSafeInteger(dishes.reduce((sum, dish) => sum + (selection[dish.key] || 0) * dish.priceCents, 0)) ? selection : {};
    } catch (_) { return {}; }
  }
  function readDeliveryCandidate(storage, now = new Date()) {
    try {
      const saved = JSON.parse(storage?.getItem(STORAGE_KEY) || 'null');
      if (!saved || saved.version !== 1 || !saved.delivery) return { candidate: null, expired: false };
      const time = Date.parse(saved.savedAt), current = now.getTime();
      if (!Number.isFinite(time) || time > current || current - time >= WEEK) return { candidate: null, expired: true };
      const { location, date, window: period } = saved.delivery;
      return { candidate: typeof location === 'string' && !!location.trim() && location.length < 200 && parseISODate(date) ? { location, date, ...(['AM', 'PM'].includes(period) ? { window: period } : {}) } : null, expired: false };
    } catch (_) { return { candidate: null, expired: false }; }
  }
  function saveSelection(storage, selection, now = new Date(), delivery = null) {
    try { storage?.setItem(STORAGE_KEY, JSON.stringify({ version: 1, savedAt: now.toISOString(), ...(delivery && typeof delivery.location === 'string' && !!delivery.location.trim() && parseISODate(delivery.date) ? { delivery: { location: delivery.location, date: delivery.date, ...(['AM', 'PM'].includes(delivery.window) ? { window: delivery.window } : {}) } } : {}), selection: Object.fromEntries(Object.entries(selection).filter(([, q]) => validQuantity(q) && q > 0)) })); } catch (_) { /* Storage denial must not stop in-memory shopping. */ }
  }
  function attrSignature(value) {
    if (!Array.isArray(value) || value.some(item => typeof item.key !== 'string' || typeof item.value !== 'string') || new Set(value.map(item => item.key)).size !== value.length) throw fail('cartChanged');
    return JSON.stringify(value.map(item => [item.key, item.value]).sort((a, b) => a[0].localeCompare(b[0])));
  }
  function verifyCart(cart, expected) {
    if (!cart || !Array.isArray(cart.lines) || cart.lines.length !== expected.length) throw fail('cartChanged');
    const seen = new Set();
    let total = 0;
    for (const line of cart.lines) {
      if (line.__typename !== 'CartLine' || !line.id || seen.has(line.merchandise?.id)) throw fail('cartChanged');
      seen.add(line.merchandise.id);
      const wanted = expected.find(item => item.merchandiseId === line.merchandise.id);
      if (!wanted || line.quantity !== wanted.quantity || attrSignature(line.attributes) !== attrSignature(wanted.attributes)) throw fail('cartChanged');
      if (!line.merchandise.availableForSale || !ruleValid(line.merchandise.quantityRule) || !validQuantity(line.quantity, line.merchandise.quantityRule)) throw fail('inventory');
      moneyToCents(line.merchandise.price);
      total += moneyToCents(line.cost?.totalAmount);
    }
    if (!Number.isSafeInteger(total) || moneyToCents(cart.cost?.subtotalAmount) !== total) throw fail('cartChanged');
    moneyToCents(cart.cost.totalAmount);
    return true;
  }

  const VARIANT_FIELDS = 'id availableForSale currentlyNotInStock price { amount currencyCode } quantityRule { minimum maximum increment }';
  const VARIANT_QUERY = `query HolidayVariants($ids: [ID!]!) @inContext(country: PH) { nodes(ids: $ids) { ... on ProductVariant { ${VARIANT_FIELDS} } } }`;
  const CART_FIELDS = `id updatedAt totalQuantity attributes { key value } cost { subtotalAmount { amount currencyCode } subtotalAmountEstimated totalAmount { amount currencyCode } totalAmountEstimated } lines(first: 100, after: $after) { pageInfo { hasNextPage endCursor } nodes { __typename ... on CartLine { id quantity attributes { key value } cost { totalAmount { amount currencyCode } } merchandise { ... on ProductVariant { ${VARIANT_FIELDS} } } } } }`;
  class StorefrontAdapter {
    constructor(config, dependencies = {}) {
      if (dependencies.readOnly === true || dependencies.previewOnly === true) readOnlyAdapters.add(this);
      this.config = config; this.fetch = dependencies.fetch || window.fetch.bind(window); this.timeoutMs = dependencies.timeoutMs || 15000;
      if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(config.shopDomain || '') || (!readOnlyAdapters.has(this) && !config.publicToken)) throw fail('configuration');
      this.endpoint = `https://${config.shopDomain}/api/2026-10/graphql.json`;
    }
    async request(query, variables) {
      if (readOnlyAdapters.has(this) && (query !== VARIANT_QUERY || !Array.isArray(variables?.ids) || variables.ids.length > 100 || variables.ids.some(id => !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(id)) || Object.keys(variables).some(key => key !== 'ids'))) throw fail('previewBlocked');
      const abort = new AbortController(), timer = setTimeout(() => abort.abort(), this.timeoutMs);
      try {
        const response = await this.fetch(this.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(!readOnlyAdapters.has(this) ? { 'X-Shopify-Storefront-Access-Token': this.config.publicToken } : {}) }, body: JSON.stringify({ query, variables }), signal: abort.signal, cache: 'no-store', credentials: 'omit' });
        if (!response.ok) throw fail('network');
        const json = await response.json();
        if (json.errors?.length) throw fail('graphql');
        if (!json.data || typeof json.data !== 'object') throw fail('network');
        return json.data;
      } catch (error) { if (error.code) throw error; throw fail(error.name === 'AbortError' ? 'timeout' : 'network'); }
      finally { clearTimeout(timer); }
    }
    async variants(ids) {
      const result = [];
      for (let i = 0; i < ids.length; i += 100) {
        const data = await this.request(VARIANT_QUERY, { ids: ids.slice(i, i + 100) });
        if (!Array.isArray(data.nodes)) throw fail('inventory');
        result.push(...data.nodes);
      }
      return result;
    }
    async read(id, options = {}) {
      if (readOnlyAdapters.has(this)) throw fail('previewBlocked');
      let after = null, combined = null; const cursors = new Set();
      do {
        const data = await this.request(`query HolidayCart($id: ID!, $after: String) @inContext(country: PH) { cart(id: $id) { ${CART_FIELDS} ${options.checkout ? 'checkoutUrl' : ''} } }`, { id, after });
        const cart = data.cart;
        if (!cart) throw fail('cartExpired');
        if (!cart.lines?.pageInfo || !Array.isArray(cart.lines.nodes) || cart.id !== id) throw fail('cartChanged');
        if (combined && (combined.updatedAt !== cart.updatedAt || JSON.stringify(combined.cost) !== JSON.stringify(cart.cost))) throw fail('cartChanged');
        if (!combined) combined = { ...cart, lines: [] };
        combined.lines.push(...cart.lines.nodes);
        if (!cart.lines.pageInfo.hasNextPage) break;
        after = cart.lines.pageInfo.endCursor;
        if (!after || cursors.has(after)) throw fail('cartChanged');
        cursors.add(after);
      } while (true);
      return combined;
    }
    async mutation(operation, declaration, args, variables) {
      if (readOnlyAdapters.has(this)) throw fail('previewBlocked');
      const data = await this.request(`mutation Holiday${operation[0].toUpperCase() + operation.slice(1)}(${declaration}, $after: String) @inContext(country: PH) { ${operation}(${args}) { cart { ${CART_FIELDS} } userErrors { code field message } warnings { code message target } } }`, { ...variables, after: null });
      const payload = data[operation];
      if (!payload) throw fail('network');
      if (payload.userErrors?.length || payload.warnings?.length) {
        const error = fail(payload.userErrors?.length ? 'inventory' : 'cartChanged');
        // An ID in a partial mutation response stays inside the adapter/active controller.
        if (payload.cart?.id) error.cartId = payload.cart.id;
        throw error;
      }
      if (!payload.cart?.id) throw fail(operation === 'cartCreate' ? 'unknownCreate' : 'cartExpired');
      if (variables.cartId && payload.cart.id !== variables.cartId) throw fail('cartChanged');
      try { return await this.read(payload.cart.id); } catch (error) { error.cartId = payload.cart.id; throw error; }
    }
    create(lines) { if (readOnlyAdapters.has(this)) return Promise.reject(fail('previewBlocked')); return this.mutation('cartCreate', '$input: CartInput!', 'input: $input', { input: { buyerIdentity: { countryCode: 'PH' }, lines } }); }
    add(id, lines) { if (readOnlyAdapters.has(this)) return Promise.reject(fail('previewBlocked')); return this.mutation('cartLinesAdd', '$cartId: ID!, $lines: [CartLineInput!]!', 'cartId: $cartId, lines: $lines', { cartId: id, lines }); }
    update(id, lines) { if (readOnlyAdapters.has(this)) return Promise.reject(fail('previewBlocked')); return this.mutation('cartLinesUpdate', '$cartId: ID!, $lines: [CartLineUpdateInput!]!', 'cartId: $cartId, lines: $lines', { cartId: id, lines }); }
    remove(id, lineIds) { if (readOnlyAdapters.has(this)) return Promise.reject(fail('previewBlocked')); return this.mutation('cartLinesRemove', '$cartId: ID!, $lineIds: [ID!]!', 'cartId: $cartId, lineIds: $lineIds', { cartId: id, lineIds }); }
  }

  class Controller {
    constructor(root, config, dependencies = {}) {
      if (config?.catalogueOnly === true) catalogueControllers.add(this);
      if (config?.preview?.enabled === true || config?.catalogueOnly === true) readOnlyControllers.add(this);
      this.root = root; this.config = config; this.fetch = dependencies.fetch || window.fetch.bind(window);
      this.clock = dependencies.clock || (() => new Date());
      try { this.storage = dependencies.storage === undefined ? window.localStorage : dependencies.storage; } catch (_) { this.storage = null; }
      if (readOnlyControllers.has(this) && this.storage) { const storage = this.storage, key = catalogueControllers.has(this) ? 'nummeals.holiday-2026.catalogue.selection.v1' : 'nummeals.holiday-2026.preview.selection.v1'; this.storage = { getItem: () => storage.getItem(key), setItem: (_, value) => storage.setItem(key, value) }; }
      const navigate = dependencies.navigate || (url => window.location.assign(url));
      this.navigate = url => { this.assertPurchasing(); return navigate(url); };
      this.confirm = dependencies.confirm || (message => window.confirm(message));
      this.requestConsent = dependencies.requestConsent || (() => this.promptConsent());
      this.adapter = dependencies.adapter || null;
      this.catalog = normalizeCatalog(config.products); this.dishes = this.catalog.dishes;
      this.selection = readSelection(this.storage, this.dishes, this.clock());
      this.deliverySequence = 0; this.deliveryPending = false; this.deliveryTask = null;
      this.delivery = null; this.proposedDelivery = null; this.savedCandidate = readDeliveryCandidate(this.storage, this.clock()); this.operations = null; this.consent = null; this.termsAcceptance = null;
      this.confirmed = null; this.revision = 0; this.confirmedRevision = -1; this.pending = false; this.error = null;
      this.ready = false; this.destroyed = false; this.queue = null; this.attempt = null; this.unknownCreate = false;
      this.filters = { 'party-tray': 'all', 'packed-meal': 'all' };
      this.cleanups = []; this.animations = new Map(); this.effects = new Map(); this.closingViews = new Set(); this.controlErrors = new Map(); this.channel = new Map();
      privateCarts.set(this, null);
      this.media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.consentContext = { config: { allergen: config.allergen?.copy, allergenUrl: config.allergen?.url }, isAllergenPromptOpen: false };
    }
    assertPurchasing() { if (readOnlyControllers.has(this)) throw fail('previewBlocked'); }
    text(key, values = {}) {
      if (key === 'previewBlocked' && catalogueControllers.has(this)) key = 'catalogueBlocked';
      const template = this.config.strings?.[key] || fallback[key] || fallback.network;
      return template.replace(/\{(\w+)\}/g, (match, name) => values[name] === undefined ? match : String(values[name]));
    }
    displayDate(date) { return parseISODate(date) ? new Intl.DateTimeFormat(this.config.locale || 'en-PH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`)) : ''; }
    money(cents) { return new Intl.NumberFormat(this.config.locale || 'en-PH', { style: 'currency', currency: 'PHP', currencyDisplay: 'narrowSymbol' }).format(cents / 100); }
    listen(target, name, handler, options) {
      if (!target?.addEventListener) return;
      target.addEventListener(name, handler, options); this.cleanups.push(() => target.removeEventListener(name, handler, options));
    }
    async init() {
      if (this.initialized) return this;
      this.initialized = true;
      this.listen(this.root, 'click', event => this.onClick(event));
      this.listen(this.root, 'focusin', event => { const day = event.target.closest('[data-holiday-calendar-day]'); if (day) { const reason = this.root.querySelector('[data-holiday-date-reason]'); if (reason) reason.textContent = day.getAttribute('aria-disabled') === 'true' ? day.title : ''; } });
      this.listen(this.root, 'focusout', () => { queueMicrotask(() => { if (!this.destroyed) this.root.querySelectorAll('[data-holiday-removed]').forEach(row => { if (!row.contains(document.activeElement)) row.remove(); }); }); });
      this.root.querySelectorAll('[data-holiday-terms-accept]').forEach(node => { node.checked = false; });
      this.listen(this.root, 'change', event => {
        if (event.target.matches('[data-holiday-terms-accept]')) {
          this.termsAcceptance = event.target.checked ? { version: HOLIDAY_TERMS_VERSION, at: this.clock().toISOString() } : null;
          this.invalidateNavigation();
          if (!event.target.checked) this.consent = null;
          if (this.error === 'termsRequired') this.error = null;
          this.render();
        }
        if (event.target.closest('[data-holiday-delivery-body]')) { if (event.target.name === 'holiday_location') this.calendarMonth = null; this.applyDeliveryDraft(); }
      });
      this.listen(this.media, 'change', () => { if (this.media.matches) this.cancelAnimations(); });
      this.listen(window, 'popstate', () => { this.readFilters(); this.renderFilters(); });
      const revealTerms = () => { if (window.location.hash === '#holiday-terms') { const terms = this.root.querySelector('#holiday-terms'); if (terms) { terms.open = true; this.scrollToSection(terms); } } };
      this.listen(window, 'hashchange', revealTerms);
      revealTerms();
      this.listen(window, 'pageshow', () => this.revalidate());
      this.listen(window, 'pagehide', event => { if (event.persisted) this.cancelAnimations(); else this.destroy(); });
      this.listen(document, 'visibilitychange', () => { if (!document.hidden) { this.revalidate(); this.refreshRegularContext(); } });
      this.listen(this.root, 'keydown', event => { this.calendarKeydown(event); this.trapFocus(event); });
      this.root.querySelectorAll('dialog:not([data-holiday-guide])').forEach(dialog => this.listen(dialog, 'cancel', event => {
        event.preventDefault();
        this.closeDialog(dialog);
      }));
      this.readFilters(); this.render(); this.renderFilters(); this.observeProductRails(); this.observeChapters(); this.observeRegularHeader(); this.observeViewport();
      this.midnightTimer = setInterval(() => this.revalidate(), 30000);
      this.cleanups.push(() => clearInterval(this.midnightTimer));
      await Promise.all([this.load(), this.refreshRegularContext()]);
      return this;
    }
    async load() {
      this.ready = false; this.error = null; this.render();
      if (validateConfig(this.config).length) { this.error = 'configuration'; this.render(); return; }
      try {
        this.adapter ||= new StorefrontAdapter(this.config.storefront, { fetch: this.fetch, readOnly: readOnlyControllers.has(this) });
        this.operations = await this.fetchOperations();
        if (this.destroyed) return;
        await this.refreshVariants(false);
        if (this.destroyed) return;
        this.ready = true; this.populateLocations(); this.revalidate(); this.render(); if (this.root.dataset.holidayView === 'review' && (this.proposedDelivery || this.savedCandidate?.candidate)) this.applyDeliveryDraft(this.proposedDelivery || this.savedCandidate.candidate); const status = this.root.querySelector('[data-holiday-status]'); if (status) status.textContent = '';
      } catch (error) { if (!this.destroyed) { this.error = error.code || 'operationalError'; this.render(); } }
    }
    async fetchOperations() {
        const base = this.config.operations.supabaseUrl, key = this.config.operations.publicKey;
        const headers = { apikey: key, Authorization: `Bearer ${key}` };
        const read = async path => {
          const abort = new AbortController(), timer = setTimeout(() => abort.abort(), 15000);
          try { const response = await this.fetch(`${base}/rest/v1/${path}`, { headers, cache: 'no-store', credentials: 'omit', signal: abort.signal }); if (!response.ok) throw fail('operationalError'); const rows = await response.json(); if (!Array.isArray(rows)) throw fail('operationalError'); return rows; }
          catch (_) { throw fail('operationalError'); } finally { clearTimeout(timer); }
        };
        const [locations, blocks, overrides] = await Promise.all([
          read('shipping_locations?select=*&is_active=eq.true&order=location_name.asc'),
          read('delivery_schedule_blocks?select=blocked_date'),
          read('delivery_schedule_period_overrides?select=target_date,allowed_timing')
        ]);
        return validateOperations({ locations, blocks, overrides });
    }
    async refreshVariants(checkChanges = true, options = {}) {
      const operations = options.operations || this.operations;
      const ids = [...new Set([...this.dishes.flatMap(dish => Object.values(dish.variants).map(variant => variant.id)), ...(operations?.locations || []).map(location => gid(location.shopify_variant_id))].filter(Boolean))];
      const nodes = await this.adapter.variants(ids), channel = new Map();
      if (options.isCurrent && !options.isCurrent()) return false;
      for (const node of nodes) { if (!node?.id || !ids.includes(node.id) || channel.has(node.id)) continue; channel.set(node.id, node); }
      let changed = false;
      for (const dish of this.dishes) {
        for (const variant of Object.values(dish.variants)) {
          const node = channel.get(variant.id);
          if (!node) { variant.available = false; continue; }
          let price;
          try { price = moneyToCents(node.price); if (!ruleValid(node.quantityRule)) throw fail('inventory'); }
          catch (_) { variant.available = false; continue; }
          if (this.selection[dish.key] && variant.priceCents !== price) changed = true;
          variant.priceCents = price; variant.quantityRule = node.quantityRule;
          variant.available = node.availableForSale === true;
        }
        const am = dish.variants.AM, pm = dish.variants.PM;
        if (am && pm && am.priceCents !== pm.priceCents) dish.channelError = 'priceChanged';
        else { dish.channelError = null; if (am) dish.priceCents = am.priceCents; }
      }
      this.channel = channel;
      if (checkChanges && changed) { this.revision++; this.confirmedRevision = -1; this.consent = null; this.render(); throw fail('priceChanged'); }
      return true;
    }
    getState() {
      return JSON.parse(JSON.stringify({ selection: this.selection, delivery: this.delivery, savedCandidate: this.savedCandidate, proposedDelivery: this.proposedDelivery, deliveryPending: this.deliveryPending, confirmed: this.confirmed, revision: this.revision, confirmedRevision: this.confirmedRevision, pending: this.pending, error: this.error, ready: this.ready, previewOnly: readOnlyControllers.has(this) && !catalogueControllers.has(this), catalogueOnly: catalogueControllers.has(this), canCheckout: this.checkoutEligible() }));
    }
    hasTermsAcceptance() { return this.termsAcceptance?.version === HOLIDAY_TERMS_VERSION && Number.isFinite(Date.parse(this.termsAcceptance.at)); }
    hasCurrentConsent() { return this.consent?.version === this.config.allergen.version && Number.isFinite(Date.parse(this.consent.at)); }
    checkoutEligible() {
      return !readOnlyControllers.has(this) && this.hasTermsAcceptance() && this.hasCurrentConsent() && this.ready && !this.pending && !this.error && !this.unknownCreate && !this.proposedDelivery && Object.keys(this.selection).length > 0 && this.confirmedRevision === this.revision && !!this.confirmed && validateDelivery(this.delivery, this.config, this.operations, this.clock()).valid;
    }
    revalidate() {
      if (this.destroyed) return;
      if (manilaToday(this.clock()) > ORDERING_LAST_DATE) this.error = 'orderingClosed';
      if (this.delivery && !this.proposedDelivery && !this.deliveryPending) {
        const result = validateDelivery(this.delivery, this.config, this.operations, this.clock());
        if (!result.valid) { this.error = result.error; this.confirmedRevision = -1; }
      }
      if (this.savedCandidate?.candidate) { const validation = resolveDelivery(this.savedCandidate.candidate, this.config, this.operations, this.clock(), this.selection, this.dishes); this.savedCandidate.error = validation.valid ? null : validation.error; }
      this.updateDeliveryOptions(); this.render();
    }
    invalidateNavigation() { this.revision++; this.confirmedRevision = -1; }
    setQuantity(key, quantity, options = {}) {
      const dish = this.dishes.find(item => item.key === key), old = this.selection[key] || 0;
      if (!dish || !validQuantity(quantity) || !Number.isSafeInteger(quantity * dish.priceCents)) return false;
      const total = this.dishes.reduce((sum, item) => sum + (item.key === key ? quantity : (this.selection[item.key] || 0)) * item.priceCents, 0);
      if (!Number.isSafeInteger(total)) return false;
      const candidates = Object.values(dish.variants);
      if (quantity > old && manilaToday(this.clock()) > ORDERING_LAST_DATE) { this.error = 'orderingClosed'; this.render(); return false; }
      if (quantity > old && (!this.ready || dish.error || dish.channelError || !this.quantityVariant(dish, quantity))) {
        this.controlErrors.set(key, quantity > old && candidates.some(variant => variant?.available) ? 'limit' : 'unavailable');
        this.announce(this.text(this.controlErrors.get(key))); this.render(); return false;
      }
      if (quantity === old) return true;
      if (quantity) this.selection[key] = quantity; else delete this.selection[key];
      this.controlErrors.delete(key); this.revision++; this.error = null;
      saveSelection(this.storage, this.selection, this.clock(), this.proposedDelivery || this.delivery || this.savedCandidate?.candidate);
      this.render({ key, old, quantity, keyboard: !!options.keyboard });
      const unit = dish.format === 'packed-meal' ? this.text(quantity === 1 ? 'setOne' : 'setMany') : this.text(quantity === 1 ? 'trayOne' : 'trayMany');
      this.announce(quantity ? (dish.format === 'packed-meal' ? `${dish.name}: ${this.packText(quantity)}` : this.text('added', { dish: dish.name, quantity, unit })) : this.text('removed', { dish: dish.name }));
      if (this.proposedDelivery || this.delivery) this.applyDeliveryDraft(this.proposedDelivery || this.delivery);
      else this.updateDeliveryOptions();
      if (!this.proposedDelivery && !this.deliveryPending && privateCarts.get(this) && this.consent) this.schedule();
      return true;
    }
    quantityVariant(dish, quantity) {
      const slot = this.proposedDelivery || this.delivery;
      if (slot?.location && slot?.date) {
        const selection = { ...this.selection, [dish.key]: quantity };
        const result = resolveDelivery(slot, this.config, this.operations, this.clock(), selection, this.dishes);
        return result.valid ? dish.variants[result.slot.window] : null;
      }
      return Object.values(dish.variants).find(variant => variant?.available && validQuantity(quantity, variant.quantityRule));
    }
    nextQuantity(dish) {
      const quantity = this.selection[dish.key] || 0;
      const choices = Object.values(dish.variants).filter(variant => variant?.available).map(variant => quantity ? quantity + (variant.quantityRule?.increment || 1) : variant.quantityRule?.minimum || 1);
      return [...new Set(choices)].sort((a, b) => a - b).find(next => this.quantityVariant(dish, next));
    }
    deliveryMissingField(slot = this.proposedDelivery || this.delivery || this.readDeliveryFields()) {
      if (!slot?.location) return 'location';
      if (!slot?.date) return 'date';
      return null;
    }
    focusDeliveryField(field = this.deliveryMissingField() || 'location') {
      const panel = this.root.querySelector('[data-holiday-delivery]');
      const selector = { location: '[name="holiday_location"]', date: '[data-holiday-calendar-day][tabindex="0"]' }[field];
      const target = panel?.querySelector(selector) || panel?.querySelector('[name="holiday_location"]');
      this.scrollToSection(target, target);
    }
    applyDeliveryDraft(slot = this.readDeliveryFields()) {
      const sequence = ++this.deliverySequence, candidate = { location: slot?.location || '', date: slot?.date || '' };
      const current = () => !this.destroyed && sequence === this.deliverySequence;
      this.proposedDelivery = candidate; this.deliveryPending = false; this.previewRefreshing = false;
      this.invalidateNavigation(); this.error = null;
      this.controlErrors.forEach((value, key) => { if (value === 'unavailable') this.controlErrors.delete(key); });
      saveSelection(this.storage, this.selection, this.clock(), candidate);
      this.updateDeliveryOptions(); this.render();
      if (!this.ready || this.deliveryMissingField(candidate)) { this.deliveryTask = null; return Promise.resolve(false); }
      this.deliveryPending = true; this.render();
      const task = (async () => {
        try {
          const operations = await this.fetchOperations();
          if (!current()) return false;
          if (!await this.refreshVariants(true, { operations, isCurrent: current }) || !current()) return false;
          this.operations = operations;
          const validation = resolveDelivery(candidate, this.config, operations, this.clock(), this.selection, this.dishes);
          if (!validation.valid) {
            if (validation.error === 'unavailableSelection') Object.keys(this.selection).forEach(key => this.controlErrors.set(key, 'unavailable'));
            throw fail(validation.error);
          }
          candidate.window = validation.slot.window;
          const fee = this.channel.get(gid(validation.location.shopify_variant_id));
          if (!fee?.availableForSale || !ruleValid(fee.quantityRule) || !validQuantity(1, fee.quantityRule) || moneyToCents(fee.price) !== validation.location.price_in_cents) throw fail('feeMismatch');
          this.delivery = candidate; this.savedCandidate = { candidate: null, expired: false }; this.proposedDelivery = null; this.error = null; this.revision++;
          saveSelection(this.storage, this.selection, this.clock(), candidate);
          this.announce(this.text('deliveryUpdated', { area: validation.location.location_name, date: this.displayDate(candidate.date) }));
          return true;
        } catch (error) {
          if (current()) { this.error = error.code || 'operationalError'; this.confirmedRevision = -1; this.announce(this.text(this.error)); }
          return false;
        } finally {
          if (current()) {
            this.deliveryPending = false; this.deliveryTask = null; this.updateDeliveryOptions(); this.render();
            if (!this.proposedDelivery && !this.error && privateCarts.get(this) && this.consent) this.schedule();
          }
        }
      })();
      this.deliveryTask = task; return task;
    }
    async setDelivery(slot, options = {}) {
      this.deliverySequence++; this.deliveryPending = false; this.deliveryTask = null;
      this.proposedDelivery = { ...slot }; this.invalidateNavigation();
      const revision = this.revision;
      try { this.operations = await this.fetchOperations(); await this.refreshVariants(true); } catch (error) { this.handleError(error); return false; }
      if (this.destroyed || revision !== this.revision) return false;
      const result = validateDelivery(slot, this.config, this.operations, this.clock());
      if (!result.valid) { this.error = result.error; this.render(); return false; }
      const changed = this.delivery && JSON.stringify(this.delivery) !== JSON.stringify(slot);
      const oldLocation = this.operations.locations.find(location => location.location_slug === this.delivery?.location);
      const changeDetails = `${result.location.location_name} · ${this.displayDate(slot.date)} · ${this.money(result.location.price_in_cents)}${oldLocation && oldLocation.price_in_cents !== result.location.price_in_cents ? ` (${this.money(oldLocation.price_in_cents)} → ${this.money(result.location.price_in_cents)})` : ''}`;
      if (changed && !options.confirmed && !await this.confirm(`${this.text('confirmSlot')}\n${changeDetails}`)) { this.proposedDelivery = null; this.render(); return false; }
      const unavailable = Object.keys(this.selection).filter(key => { const dish = this.dishes.find(item => item.key === key); return dish?.error || dish?.channelError || !dish?.variants[slot.window]?.available || !validQuantity(this.selection[key], dish.variants[slot.window].quantityRule); });
      if (unavailable.length && !await this.confirm(`${this.text('removeUnavailable')}\n${unavailable.map(key => this.dishes.find(dish => dish.key === key)?.name).join(', ')}`)) { this.error = 'unavailableWindow'; this.render(); return false; }
      if (this.destroyed || revision !== this.revision) return false;
      const commitValidation = validateDelivery(slot, this.config, this.operations, this.clock());
      if (!commitValidation.valid) { this.error = commitValidation.error; this.render(); return false; }
      unavailable.forEach(key => delete this.selection[key]);
      this.delivery = { ...slot }; this.savedCandidate = { candidate: null, expired: false }; this.proposedDelivery = null; this.error = null; this.revision++;
      saveSelection(this.storage, this.selection, this.clock(), this.delivery || this.savedCandidate?.candidate); this.render();
      this.announce(this.text('deliveryUpdated', { area: result.location.location_name, date: this.displayDate(slot.date) }));
      if (privateCarts.get(this) && this.consent) this.schedule();
      return true;
    }
    expectedLines() {
      this.assertPurchasing();
      if (this.deliveryPending || this.proposedDelivery) throw fail('deliveryRequired');
      if (Object.keys(this.selection).length === 0) return [];
      const validation = validateDelivery(this.delivery, this.config, this.operations, this.clock());
      if (!validation.valid) throw fail(validation.error);
      if (!this.hasTermsAcceptance()) throw fail('termsRequired');
      if (!this.consent || this.consent.version !== this.config.allergen.version) throw fail('consentRequired');
      const fee = this.channel.get(gid(validation.location.shopify_variant_id));
      if (!fee?.availableForSale || !ruleValid(fee.quantityRule) || !validQuantity(1, fee.quantityRule) || moneyToCents(fee.price) !== validation.location.price_in_cents) throw fail('feeMismatch');
      if (Object.keys(this.selection).some(key => this.dishes.find(dish => dish.key === key)?.channelError)) throw fail('priceChanged');
      return deriveLines(this.selection, this.dishes, this.delivery, this.operations, { ...this.consent, termsVersion: this.termsAcceptance.version, termsAt: this.termsAcceptance.at });
    }
    async promptConsent() {
      this.assertPurchasing();
      if (this.destroyed) return false;
      const bag = this.root.querySelector('[data-holiday-bag]');
      this.reviewDialogContext = { bag, wasOpen: !!bag && !bag.hidden, trigger: document.activeElement };
      if (bag && !bag.hidden) bag.inert = true;
      return ProductCartManager.prototype.showAllergenAcknowledgmentDialog.call(this.consentContext);
    }
    restoreReview() {
      const context = this.reviewDialogContext; this.reviewDialogContext = null;
      if (!context?.wasOpen || this.destroyed) return;
      context.bag.inert = false;
      if (context.trigger?.isConnected && !context.trigger.disabled) context.trigger.focus({ preventScroll: true });
    }
    async acceptConsent() {
      this.assertPurchasing();
      if (this.destroyed || !this.ready || this.proposedDelivery || !Object.keys(this.selection).length) throw fail('checkoutBlocked');
      if (!this.hasTermsAcceptance()) throw fail('termsRequired');
      this.operations = await this.fetchOperations();
      if (this.destroyed) throw fail('checkoutBlocked');
      await this.refreshVariants(true);
      if (this.destroyed) throw fail('checkoutBlocked');
      const validation = validateDelivery(this.delivery, this.config, this.operations, this.clock());
      if (!validation.valid) throw fail(validation.error);
      if (this.hasCurrentConsent()) return true;
      const revision = this.revision;
      const accepted = await this.requestConsent();
      if (!accepted) return false;
      if (this.destroyed || revision !== this.revision) throw fail('checkoutBlocked');
      this.consent = { version: this.config.allergen.version, at: this.clock().toISOString() };
      this.revision++; this.error = null;
      return true;
    }
    submit() { return this.runAttempt(false); }
    checkout() { return this.runAttempt(true); }
    runAttempt(navigate) {
      if (readOnlyControllers.has(this)) { this.handleError(fail('previewBlocked')); return Promise.resolve(false); }
      if (this.attempt) return this.attempt;
      this.attempt = Promise.resolve().then(async () => {
        let navigated = false; this.render();
        try {
          if (this.unknownCreate) throw fail('unknownCreate');
          if (!await this.acceptConsent()) return false;
          await this.schedule();
          if (!this.checkoutEligible()) return false;
          if (!navigate) return true;
          const revision = this.revision;
          this.operations = await this.fetchOperations();
          if (this.destroyed || revision !== this.revision) throw fail('checkoutBlocked');
          this.fetchingCheckout = true; this.render();
          const expected = this.expectedLines(), cart = await this.adapter.read(privateCarts.get(this), { checkout: true });
          if (this.destroyed || revision !== this.revision || this.pending || this.confirmedRevision !== revision) throw fail('checkoutBlocked');
          verifyCart(cart, expected); this.verifyPrices(cart);
          const valid = validateDelivery(this.delivery, this.config, this.operations, this.clock());
          if (!valid.valid) throw fail(valid.error);
          const url = validateCheckoutUrl(cart.checkoutUrl, this.config.storefront.checkoutOrigins);
          this.navigate(url); navigated = true; return true;
        } catch (error) { this.handleError(error); return false; }
        finally { this.fetchingCheckout = false; this.attempt = null; this.render(); if (!navigated) this.restoreReview(); else this.reviewDialogContext = null; }
      });
      return this.attempt;
    }
    schedule() {
      if (readOnlyControllers.has(this)) return Promise.reject(fail('previewBlocked'));
      if (this.queue) return this.queue;
      this.pending = true; this.render();
      this.queue = this.reconcile().catch(error => this.handleError(error)).finally(() => { this.pending = false; this.queue = null; this.render(); });
      return this.queue;
    }
    async whenIdle() { while (this.queue || this.deliveryTask) await (this.deliveryTask || this.queue); }
    async reconcile() {
      this.assertPurchasing();
      while (!this.destroyed && this.confirmedRevision !== this.revision) {
        if (!this.ready || this.unknownCreate) throw fail(this.unknownCreate ? 'unknownCreate' : 'configuration');
        await this.refreshVariants(true);
        if (this.destroyed) return;
        const revision = this.revision, expected = this.expectedLines();
        let id = privateCarts.get(this), cart;
        if (!id && !expected.length) { this.confirmedRevision = revision; this.confirmed = null; return; }
        try {
          if (!id) {
            cart = await this.adapter.create(expected);
            if (!cart?.id) throw fail('unknownCreate');
            if (this.destroyed) return;
            privateCarts.set(this, cart.id); id = cart.id;
          } else {
            cart = await this.adapter.read(id);
            if (this.destroyed) return;
            this.validateKnownLines(cart);
            const remove = cart.lines.filter(line => !expected.some(wanted => wanted.merchandiseId === line.merchandise.id)).map(line => line.id);
            if (remove.length) cart = await this.adapter.remove(id, remove);
            if (this.destroyed) return;
            this.validateKnownLines(cart);
            const updates = expected.flatMap(wanted => {
              const line = cart.lines.find(current => current.merchandise.id === wanted.merchandiseId);
              return line && (line.quantity !== wanted.quantity || attrSignature(line.attributes) !== attrSignature(wanted.attributes)) ? [{ id: line.id, quantity: wanted.quantity, attributes: wanted.attributes, merchandiseId: wanted.merchandiseId }] : [];
            });
            if (updates.length) cart = await this.adapter.update(id, updates);
            if (this.destroyed) return;
            this.validateKnownLines(cart);
            const adds = expected.filter(wanted => !cart.lines.some(line => line.merchandise.id === wanted.merchandiseId));
            if (adds.length) cart = await this.adapter.add(id, adds);
          }
          if (this.destroyed) return;
          // Never trust mutation payload completeness as a substitute for final readback.
          cart = await this.adapter.read(id);
        } catch (error) {
          if (this.destroyed) return;
          if (error.cartId) { privateCarts.set(this, error.cartId); id = error.cartId; }
          if (!id) { if (['timeout', 'network', 'unknownCreate'].includes(error.code)) { this.unknownCreate = true; throw fail('unknownCreate'); } throw error; }
          if (error.code === 'cartExpired') throw error;
          // Even explicit errors/warnings can accompany partial writes. Keep a
          // fresh observed snapshot for recovery, without declaring it committed.
          cart = await this.adapter.read(id);
          this.validateKnownLines(cart);
          moneyToCents(cart.cost?.totalAmount); moneyToCents(cart.cost?.subtotalAmount);
          this.confirmed = { lines: cart.lines, cost: cart.cost };
          if (['timeout', 'network'].includes(error.code)) verifyCart(cart, expected);
          else throw error;
        }
        verifyCart(cart, expected);
        this.confirmed = { lines: cart.lines, cost: cart.cost };
        this.verifyPrices(cart);
        if (this.destroyed) return;
        this.confirmed = { lines: cart.lines, cost: cart.cost };
        this.confirmedRevision = revision; this.error = null;
        if (revision === this.revision) saveSelection(this.storage, this.selection, this.clock(), this.delivery);
        this.render();
      }
    }
    validateKnownLines(cart) {
      if (!cart || !Array.isArray(cart.lines)) throw fail('cartChanged');
      const seen = new Set(), allowed = new Set([...this.dishes.flatMap(dish => Object.values(dish.variants).map(variant => variant.id)), ...this.operations.locations.map(location => gid(location.shopify_variant_id))]);
      for (const line of cart.lines) {
        const id = line.merchandise?.id;
        if (line.__typename !== 'CartLine' || !line.id || !allowed.has(id) || seen.has(id)) throw fail('cartChanged');
        const attrs = Object.fromEntries((line.attributes || []).map(attr => [attr.key, attr.value]));
        if (attrs._holiday_campaign !== '2026') throw fail('cartChanged');
        seen.add(id);
      }
    }
    verifyPrices(cart) {
      for (const line of cart.lines) {
        const node = this.channel.get(line.merchandise?.id);
        if (!node || moneyToCents(node.price) !== moneyToCents(line.merchandise.price)) {
          const dish = this.dishes.find(item => Object.values(item.variants).some(variant => variant.id === line.merchandise.id));
          if (!dish) throw fail('feeMismatch');
          const price = moneyToCents(line.merchandise.price);
          dish.priceCents = price;
          const variant = Object.values(dish.variants).find(item => item.id === line.merchandise.id);
          variant.priceCents = price;
          this.consent = null; throw fail('priceChanged');
        }
        if (line.attributes.some(attr => attr.key === '_shipfee' && attr.value === 'true')) {
          const location = this.operations.locations.find(row => gid(row.shopify_variant_id) === line.merchandise.id);
          if (!location || moneyToCents(line.merchandise.price) !== location.price_in_cents || moneyToCents(line.cost.totalAmount) !== location.price_in_cents) throw fail('feeMismatch');
        }
      }
    }
    handleError(error) {
      if (this.destroyed) return;
      this.error = error.code || 'network'; this.confirmedRevision = -1;
      if (this.error === 'cartExpired') { privateCarts.set(this, null); this.consent = null; this.confirmed = null; }
      saveSelection(this.storage, this.selection, this.clock(), this.delivery || this.savedCandidate?.candidate); this.render(); this.announce(this.text(this.error));
    }
    async refreshPreview() {
      if (!readOnlyControllers.has(this) || this.deliveryPending) return;
      const sequence = this.deliverySequence, current = () => !this.destroyed && sequence === this.deliverySequence;
      this.previewRefreshing = true; this.render();
      try {
        const operations = await this.fetchOperations(); if (!current()) return;
        if (!await this.refreshVariants(true, { operations, isCurrent: current }) || !current()) return;
        this.operations = operations; this.revalidate();
      } catch (error) { if (current()) this.handleError(error); }
      finally { if (current()) { this.previewRefreshing = false; this.render(); } }
    }
    async retry() {
      if (readOnlyControllers.has(this)) return this.load();
      const previousError = this.error;
      this.error = null;
      if (previousError === 'cartChanged') {
        // An explicit retry discards the unreadable or mixed isolated cart only.
        // Recover desired dishes into a fresh cart after fresh consent.
        privateCarts.set(this, null); this.consent = null; this.confirmed = null; this.confirmedRevision = -1;
      }
      if (!this.ready) { await this.load(); return; }
      if (this.unknownCreate) { this.unknownCreate = false; privateCarts.set(this, null); this.consent = null; }
      if (!privateCarts.get(this) || !this.consent) return this.submit();
      return this.schedule();
    }
    announce(message) {
      clearTimeout(this.announcementTimer);
      this.announcementTimer = setTimeout(() => { const node = this.root.querySelector('[data-holiday-status]'); if (node && !this.destroyed) { node.classList.add('visually-hidden'); node.textContent = message; } }, 120);
    }
    packText(quantity) {
      return this.text('packSummary', { sets: `${quantity} ${this.text(quantity === 1 ? 'setOne' : 'setMany')}`, packs: quantity * 20 });
    }
    render(change) {
      if (this.destroyed || !this.root) return;
      let subtotal = 0, trays = 0, sets = 0, rice = 0;
      for (const dish of this.dishes) { const q = this.selection[dish.key] || 0; subtotal += q * dish.priceCents; if (dish.format === 'packed-meal') sets += q; else if (dish.format === 'rice') rice += q; else trays += q; }
      this.root.dataset.holidayState = this.pending ? 'updating' : this.error ? 'error' : !readOnlyControllers.has(this) && this.confirmedRevision === this.revision ? 'confirmed' : 'selecting';
      this.root.querySelectorAll('[data-holiday-card]').forEach(card => {
        const key = card.dataset.dishKey, dish = this.dishes.find(item => item.key === key);
        if (dish) {
          if (!dish.error) {
            const price = card.querySelector('.holiday-edit__price strong');
            if (price) price.textContent = this.money(dish.priceCents);
            const perPack = card.querySelector('.holiday-edit__per-pack');
            if (perPack && dish.format === 'packed-meal') perPack.textContent = this.text('perPack', { price: this.money(dish.priceCents / 20) });
          }
          this.renderQuantity(card, dish, change?.key === key ? change : null);
        }
      });
      this.renderLines(change);
      const count = trays + rice + sets;
      this.root.dataset.holidayHasItems = String(count > 0);
      this.root.querySelectorAll('.holiday-edit__mobile-bag').forEach(node => { node.hidden = count === 0 || this.root.dataset.holidayView === 'review'; });
      this.root.querySelectorAll('[data-holiday-count]').forEach(node => { node.textContent = String(count); });
      this.root.querySelectorAll('[data-holiday-item-unit]').forEach(node => { node.textContent = this.text(count === 1 ? 'itemOne' : 'itemMany'); });
      const summary = [trays ? `${trays} ${this.text(trays === 1 ? 'partyOne' : 'partyMany')}` : '', rice ? `${rice} ${this.text(rice === 1 ? 'riceOne' : 'riceMany')}` : '', sets ? `${sets} ${this.text(sets === 1 ? 'setOne' : 'setMany')} (${sets * 20} packs)` : ''].filter(Boolean).join(' · ');
      this.root.querySelectorAll('[data-holiday-summary]').forEach(node => { node.textContent = count ? summary : ''; node.hidden = !count; });
      this.root.querySelectorAll('[data-holiday-action="open-bag"]').forEach(button => { button.disabled = false; button.setAttribute('aria-label', `${button.classList.contains('holiday-edit__bag-view') ? this.text('viewCart') + ' — ' : ''}${this.text(count === 1 ? 'bagNameOne' : 'bagName', { count })}`); });
      const delivery = this.proposedDelivery || this.deliveryPending ? { valid: false } : validateDelivery(this.delivery, this.config, this.operations, this.clock());
      let fee = delivery.valid && count ? delivery.location.price_in_cents : null;
      const previewPricesValid = !this.deliveryPending && (!readOnlyControllers.has(this) || (this.ready && !this.previewRefreshing && !this.error && Object.entries(this.selection).every(([key, quantity]) => {
        const dish = this.dishes.find(item => item.key === key), variants = this.delivery ? [dish?.variants[this.delivery.window]] : Object.values(dish?.variants || {});
        return dish && !dish.error && !dish.channelError && variants.some(variant => variant?.available && validQuantity(quantity, variant.quantityRule));
      })));
      if (!previewPricesValid) fee = null;
      if (readOnlyControllers.has(this) && fee !== null) {
        const variant = this.channel.get(gid(delivery.location.shopify_variant_id));
        try { if (variant?.availableForSale !== true || !ruleValid(variant.quantityRule) || !validQuantity(1, variant.quantityRule) || moneyToCents(variant.price) !== fee) fee = null; } catch (_) { fee = null; }
      }
      this.root.querySelectorAll('[data-holiday-total-row] dt').forEach(node => { if (readOnlyControllers.has(this)) node.textContent = this.text('previewEstimate'); });
      this.root.querySelectorAll('[data-holiday-total-row]').forEach(node => { node.hidden = fee === null; });
      const authoritative = !readOnlyControllers.has(this) && this.confirmed && this.confirmedRevision === this.revision && !this.pending;
      const shownSubtotal = authoritative ? this.confirmed.lines.filter(line => !line.attributes.some(attr => attr.key === '_shipfee' && attr.value === 'true')).reduce((sum, line) => sum + moneyToCents(line.cost.totalAmount), 0) : subtotal;
      this.root.querySelectorAll('[data-holiday-commit-status]').forEach(node => { node.textContent = count && !readOnlyControllers.has(this) ? this.text(authoritative ? 'committed' : 'pending') : ''; node.hidden = !node.textContent; });
      this.root.querySelectorAll('[data-holiday-sticky-subtotal]').forEach(node => { node.textContent = previewPricesValid ? this.money(shownSubtotal) : '—'; });
      this.root.querySelectorAll('[data-holiday-sticky-amount]').forEach(node => { node.textContent = previewPricesValid ? this.money(subtotal + (fee ?? 0)) : '—'; });
      this.root.querySelectorAll('[data-holiday-sticky-label]').forEach(node => { node.textContent = this.text(fee === null ? 'estimatedSubtotal' : 'previewEstimate'); });
      this.root.querySelectorAll('[data-holiday-sticky-note]').forEach(node => { node.textContent = this.text(fee === null ? 'deliveryExtra' : 'deliveryIncluded'); });
      this.root.querySelectorAll('[data-holiday-catalogue-notice]').forEach(node => { node.hidden = !catalogueControllers.has(this) || !count; });
      this.root.querySelectorAll('[data-holiday-subtotal]').forEach(node => { node.textContent = count && previewPricesValid ? this.money(shownSubtotal) : '—'; });
      this.root.querySelectorAll('[data-holiday-fee]').forEach(node => { node.textContent = fee !== null ? this.money(fee) : this.text(this.deliveryPending ? 'checkingDelivery' : !delivery.valid ? 'chooseFee' : this.previewRefreshing ? 'checking' : 'feeMismatch'); });
      this.root.querySelectorAll('[data-holiday-total]').forEach(node => { node.textContent = authoritative ? this.money(moneyToCents(this.confirmed.cost.totalAmount)) : count && fee !== null ? this.money(subtotal + fee) : '—'; });
      this.root.querySelectorAll('[data-holiday-delivery-summary]').forEach(node => { node.textContent = delivery.valid ? `${delivery.location.location_name} · ${this.displayDate(this.delivery.date)} · ${this.text('deliveryWindow')}` : this.text(node.hasAttribute('data-holiday-delivery-intro') ? 'deliveryPrompt' : 'chooseDelivery'); });
      this.root.querySelectorAll('[data-holiday-delivery-error]').forEach(node => { node.dataset.deliveryChecking = String(this.deliveryPending); node.textContent = this.deliveryPending ? this.text('checkingDelivery') : this.error ? this.text(this.error) : this.savedCandidate?.expired ? this.text('expiredCandidate') : this.savedCandidate?.candidate ? `${this.text('candidateNotice')}${this.savedCandidate.error ? ' ' + this.text(this.savedCandidate.error) : ''}` : ''; });
      this.root.querySelectorAll('[data-holiday-bag-error]').forEach(node => { node.textContent = this.error ? this.text(this.error) : this.pending ? this.text('updating') : ''; node.hidden = !node.textContent; });
      this.root.querySelectorAll('[data-holiday-empty]').forEach(node => { node.hidden = count > 0; });
      this.root.querySelectorAll('[data-holiday-bag] .holiday-edit__totals, [data-holiday-bag] .holiday-edit__bag-delivery').forEach(node => { node.hidden = !count; });
      this.root.querySelectorAll('[data-holiday-action="checkout"], [data-holiday-action="review"]').forEach(button => {
        button.disabled = readOnlyControllers.has(this) || !this.ready || !count || !delivery.valid || this.pending || !!this.attempt || !!this.fetchingCheckout || this.unknownCreate || !!this.proposedDelivery;
      });
      this.root.querySelectorAll('[data-holiday-action="retry"]').forEach(button => { button.hidden = !this.error; button.disabled = this.pending || !!this.attempt; });
      this.root.querySelectorAll('[data-holiday-action="edit-delivery"]').forEach(button => { button.disabled = !this.ready; });
      this.root.querySelectorAll('[data-holiday-action="delivery-retry"]').forEach(button => { button.hidden = !this.error; button.disabled = this.deliveryPending; });
      this.root.querySelectorAll('[data-holiday-action="confirm-delivery"]').forEach(button => { button.disabled = !this.ready; });
      for (const [field, value] of Object.entries({ area: delivery.valid ? delivery.location.location_name : '', date: delivery.valid ? this.displayDate(this.delivery.date) : '' })) {
        this.root.querySelectorAll(`[data-holiday-${field}-value]`).forEach(node => { node.dataset.placeholder ||= node.textContent; node.textContent = value || node.dataset.placeholder; });
      }
      this.root.querySelectorAll('[data-holiday-action="bag-primary"]').forEach(button => {
        const missing = this.deliveryMissingField(), guidance = { location: 'chooseAreaFirst', date: 'chooseDateFirst' }[missing];
        const checking = this.deliveryPending || this.pending || this.attempt;
        button.textContent = this.text(checking ? 'checkingDelivery' : !count ? 'browse' : !this.ready || this.error ? 'reviewChanges' : guidance || (!delivery.valid ? 'reviewChanges' : readOnlyControllers.has(this) ? 'checkoutUnavailable' : !this.hasCurrentConsent() ? 'reviewConsent' : 'checkout'));
        button.disabled = !!checking || (!!count && delivery.valid && !this.error && readOnlyControllers.has(this));
      });
      this.root.querySelectorAll('[data-holiday-terms-error]').forEach(node => { node.textContent = this.error === 'termsRequired' ? this.text('termsRequired') : ''; node.hidden = this.error !== 'termsRequired'; });
      this.root.querySelectorAll('[data-holiday-consent-guidance]').forEach(node => { node.hidden = readOnlyControllers.has(this) || !count || !delivery.valid || this.hasCurrentConsent(); });
      this.measureViewport?.();

    }
    renderQuantity(container, dish, change) {
      const quantity = this.selection[dish.key] || 0, candidates = Object.values(dish.variants);
      const unavailable = !!(dish.error || dish.channelError) || !candidates.some(variant => variant?.available);
      const unit = dish.format === 'packed-meal' ? this.text('setMany') : this.text('trayMany');
      const addText = this.text(dish.format === 'packed-meal' ? 'addPacked' : dish.format === 'rice' ? 'addRice' : 'addTray');
      const plus = container.querySelector('[data-holiday-action="add"], [data-holiday-action="increase"]');
      const minus = container.querySelector('[data-holiday-action="decrease"]');
      container.dataset.quantity = String(quantity);
      if (plus) {
        plus.dataset.holidayAction = quantity ? 'increase' : 'add';
        const next = this.nextQuantity(dish);
        plus.disabled = manilaToday(this.clock()) > ORDERING_LAST_DATE || !this.ready || unavailable || next === undefined;
        plus.setAttribute('aria-label', quantity ? this.text('increase', { dish: dish.name, unit }) : `${addText}: ${dish.name}`);
      }
      if (minus) { const hadFocus = document.activeElement === minus; minus.hidden = quantity === 0; minus.disabled = quantity === 0; minus.tabIndex = quantity ? 0 : -1; minus.setAttribute('aria-label', this.text('decrease', { dish: dish.name, unit })); if (!quantity && hadFocus && plus) plus.focus({ preventScroll: true }); }
      container.querySelectorAll('[data-holiday-quantity]').forEach(node => { node.textContent = String(quantity); });
      container.querySelectorAll('[data-holiday-roll]').forEach(node => {
        const text = unavailable && !quantity ? this.text('unavailable') : quantity ? String(quantity) : addText;
        const old = node.dataset.value ?? node.textContent; node.dataset.value = text;
        if (old !== text) this.roll(node, old, text, change);
      });
      container.querySelectorAll('[data-holiday-pack-summary]').forEach(node => { node.textContent = dish.format === 'packed-meal' && quantity ? this.packText(quantity) : ''; });
      container.querySelectorAll('[data-holiday-error]').forEach(node => { const code = this.controlErrors.get(dish.key) || (unavailable ? 'unavailable' : null); node.textContent = code ? this.text(code) : '';
        if (this.error && this.confirmed) { const actual = this.confirmed.lines.find(line => line.attributes.some(attr => attr.key === '_holiday_dish_key' && attr.value === dish.dishKey) && line.attributes.some(attr => attr.key === '_holiday_format' && attr.value === dish.format))?.quantity || 0; if (actual !== quantity) node.textContent += ` ${this.text('confirmedQuantity', { quantity: actual, requested: quantity })}`; }
        node.hidden = !node.textContent; });
    }
    roll(node, previous, next, change) {
      const running = this.animations.get(node);
      if (running) { running.forEach(animation => animation.cancel()); this.animations.delete(node); node.textContent = next; return; }
      node.textContent = next;
      if (!change || change.keyboard || this.media?.matches || typeof node.animate !== 'function') return;
      const outgoing = document.createElement('span'), incoming = document.createElement('span');
      outgoing.textContent = previous; incoming.textContent = next;
      outgoing.className = 'holiday-edit__roll-out'; incoming.className = 'holiday-edit__roll-in';
      node.replaceChildren(outgoing, incoming);
      const sign = change.quantity > change.old ? 1 : -1;
      const options = { duration: 160, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' };
      try {
        const animations = [outgoing.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: `translateY(${-sign * 100}%)`, opacity: 0 }], options), incoming.animate([{ transform: `translateY(${sign * 100}%)`, opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }], options)];
        this.animations.set(node, animations);
        Promise.all(animations.map(animation => animation.finished)).then(() => { if (this.animations.get(node) === animations) { node.textContent = node.dataset.value; this.animations.delete(node); } }).catch(() => {});
      } catch (_) { node.textContent = next; this.animations.delete(node); }
    }
    cancelAnimations() {
      this.effects.forEach(animation => animation.cancel()); this.effects.clear();
      this.closingViews.forEach(node => { this.effects.get(node.firstElementChild)?.cancel(); this.effects.delete(node.firstElementChild); node.remove(); }); this.closingViews.clear();
      this.animations.forEach((animations, node) => { animations.forEach(animation => animation.cancel()); node.textContent = node.dataset.value; }); this.animations.clear();
    }
    renderLines(change) {
      const target = this.root.querySelector('[data-holiday-lines]'); if (!target) return;
      const wanted = Object.keys(this.selection);
      target.querySelectorAll('[data-holiday-line]').forEach(node => {
        if (wanted.includes(node.dataset.dishKey)) { node.removeAttribute('data-holiday-removed'); return; }
        const dish = this.dishes.find(item => item.key === node.dataset.dishKey);
        if (dish && node.contains(document.activeElement)) {
          // Retain the focused representation until focus leaves, so the disappearing
          // minus can hand focus to this same dish's Add control inside the dialog.
          node.dataset.holidayRemoved = ''; this.renderQuantity(node, dish, change);
          node.querySelector('[data-holiday-line-price]').textContent = this.text('removed', { dish: dish.name });
        } else node.remove();
      });
      for (const key of wanted) {
        const dish = this.dishes.find(item => item.key === key); if (!dish) continue;
        let row = Array.from(target.querySelectorAll('[data-holiday-line]')).find(node => node.dataset.dishKey === key);
        if (!row) {
          row = document.createElement('div'); row.className = 'holiday-edit__bag-line'; row.dataset.holidayLine = ''; row.dataset.dishKey = key;
          if (dish.image) { const image = document.createElement('img'); image.className = 'holiday-edit__line-image'; image.src = dish.image; image.alt = ''; image.width = 80; image.height = 80; image.loading = 'lazy'; row.append(image); }
          const name = document.createElement('h3'); name.textContent = dish.name; row.append(name);
          const unit = document.createElement('p'); unit.className = 'holiday-edit__line-unit'; unit.textContent = this.text(dish.format === 'packed-meal' ? 'packedUnit' : dish.format === 'rice' ? 'riceOne' : 'partyOne'); row.append(unit);
          const math = document.createElement('p'); math.className = 'holiday-edit__line-math'; math.dataset.holidayLineMath = ''; row.append(math);
          const price = document.createElement('p'); price.dataset.holidayLinePrice = ''; row.append(price);
          const control = document.createElement('div'); control.className = 'holiday-edit__quantity';
          const minus = document.createElement('button'); minus.type = 'button'; minus.className = 'holiday-edit__minus'; minus.dataset.holidayAction = 'decrease'; minus.dataset.dishKey = key; minus.textContent = '−';
          const semantic = document.createElement('span'); semantic.dataset.holidayQuantity = ''; semantic.className = 'visually-hidden';
          const roll = document.createElement('span'); roll.dataset.holidayRoll = ''; roll.setAttribute('aria-hidden', 'true'); roll.className = 'holiday-edit__roll';
          const plus = document.createElement('button'); plus.type = 'button'; plus.className = 'holiday-edit__add'; plus.dataset.holidayAction = 'increase'; plus.dataset.dishKey = key;
          const viewport = document.createElement('span'); viewport.className = 'holiday-edit__roll-viewport'; viewport.append(roll);
          const symbol = document.createElement('span'); symbol.className = 'holiday-edit__plus'; symbol.textContent = '+'; symbol.setAttribute('aria-hidden', 'true');
          plus.append(viewport, symbol); control.append(minus, semantic, plus); row.append(control);
          const packs = document.createElement('p'); packs.dataset.holidayPackSummary = ''; row.append(packs);
          const error = document.createElement('p'); error.dataset.holidayError = ''; row.append(error); target.append(row);
        }
        const confirmedLine = this.confirmedRevision === this.revision && !this.pending ? this.confirmed?.lines.find(line => line.attributes.some(attr => attr.key === '_holiday_dish_key' && attr.value === dish.dishKey) && line.attributes.some(attr => attr.key === '_holiday_format' && attr.value === dish.format)) : null;
        row.querySelector('[data-holiday-line-price]').textContent = this.money(confirmedLine ? moneyToCents(confirmedLine.cost.totalAmount) : dish.priceCents * this.selection[key]);
        row.querySelector('[data-holiday-line-math]').textContent = `${this.money(dish.priceCents)} × ${this.selection[key]}`;
        this.renderQuantity(row, dish, change?.key === key ? change : null);
      }
      const ordered = [];
      for (const [format, title] of [['party-tray', 'groupsParty'], ['packed-meal', 'groupsPacked'], ['rice', 'groupsRice']]) {
        const rows = this.dishes.filter(dish => dish.format === format).map(dish => Array.from(target.querySelectorAll('[data-holiday-line]')).find(row => row.dataset.dishKey === dish.key)).filter(Boolean);
        let heading = target.querySelector(`[data-holiday-format-heading="${format}"]`);
        if (!rows.length) { heading?.remove(); continue; }
        if (!heading) { heading = document.createElement('h4'); heading.dataset.holidayFormatHeading = format; heading.textContent = this.text(title); }
        ordered.push(heading, ...rows);
      }
      ordered.forEach((node, index) => { if (target.children[index] !== node) target.insertBefore(node, target.children[index] || null); });
    }
    readFilters() {
      const query = new URL(window.location.href).searchParams;
      for (const [format, param] of [['party-tray', 'tray_category'], ['packed-meal', 'packed_category']]) {
        const value = query.get(param); this.filters[format] = PAIRS[format].includes(value) ? value : 'all';
      }
    }
    renderFilters() {
      this.root.querySelectorAll('[data-holiday-filter]').forEach(button => { button.disabled = false; button.setAttribute('aria-pressed', String(this.filters[button.dataset.format] === button.dataset.category)); });
      this.root.querySelectorAll('[data-holiday-card]').forEach(card => {
        if (card.hasAttribute('data-holiday-featured') || card.classList.contains('holiday-edit__hero-card')) return;
        const dish = this.dishes.find(item => item.key === card.dataset.dishKey);
        if (!dish || !this.filters[dish.format]) return;
        card.hidden = this.filters[dish.format] !== 'all' && dish.category !== this.filters[dish.format];
      });
      this.root.querySelectorAll('[data-holiday-product-rail]').forEach(rail => this.updateProductRail(rail));
      this.root.querySelectorAll('[data-holiday-filter-empty]').forEach(node => { const chapter = node.closest('[data-holiday-chapter]'); if (chapter) node.hidden = !chapter.querySelector('[data-holiday-card]') || !!chapter.querySelector('[data-holiday-card]:not([hidden])'); });
    }
    observeProductRails() {
      this.root.querySelectorAll('[data-holiday-product-rail]').forEach(rail => {
        this.listen(rail, 'scroll', () => this.updateProductRail(rail), { passive: true });
        this.updateProductRail(rail);
        if (typeof ResizeObserver !== 'undefined') {
          // Observe content rather than the rail: applying its height must not create a resize feedback loop.
          const observer = new ResizeObserver(() => this.updateProductRail(rail));
          rail.querySelectorAll('[data-holiday-card]').forEach(card => observer.observe(card));
          this.cleanups.push(() => observer.disconnect());
        }
      });
      this.listen(window, 'resize', () => this.root.querySelectorAll('[data-holiday-product-rail]').forEach(rail => this.updateProductRail(rail)), { passive: true });
    }
    updateProductRail(rail) {
      const chapter = rail.closest('[data-holiday-chapter]');
      if (!chapter) return;
      const cards = [...rail.querySelectorAll('[data-holiday-card]:not([hidden])')];
      const left = rail.getBoundingClientRect().left;
      let index = 0, distance = Infinity;
      cards.forEach((card, position) => { const delta = Math.abs(card.getBoundingClientRect().left - left); if (delta < distance) { index = position; distance = delta; } });
      rail.dataset.holidayRailIndex = String(index);
      const activeCard = cards[index];
      if (window.matchMedia('(max-width: 749px)').matches && activeCard) {
        const contentHeight = activeCard.getBoundingClientRect().height;
        if (contentHeight > 0) {
          const railStyle = window.getComputedStyle(rail);
          const padding = (parseFloat(railStyle.paddingTop) || 0) + (parseFloat(railStyle.paddingBottom) || 0);
          const scrollbar = Math.max(0, rail.offsetHeight - rail.clientHeight);
          rail.style.setProperty('--holiday-rail-height', `${Math.ceil(contentHeight + padding + scrollbar)}px`);
        }
      } else rail.style.removeProperty('--holiday-rail-height');
      const status = chapter.querySelector('[data-holiday-rail-status]');
      if (status) status.textContent = cards.length ? `${index + 1} of ${cards.length}` : '';
      const previous = chapter.querySelector('[data-holiday-action="rail-previous"]'), next = chapter.querySelector('[data-holiday-action="rail-next"]');
      if (previous) { previous.disabled = !cards.length || index === 0; previous.hidden = cards.length <= 1; }
      if (next) { next.disabled = !cards.length || index === cards.length - 1; next.hidden = cards.length <= 1; }
    }
    moveProductRail(button, direction) {
      const rail = button.closest('[data-holiday-chapter]')?.querySelector('[data-holiday-product-rail]');
      if (!rail) return;
      const cards = [...rail.querySelectorAll('[data-holiday-card]:not([hidden])')];
      this.updateProductRail(rail);
      const index = Math.max(0, Math.min(cards.length - 1, Number(rail.dataset.holidayRailIndex || 0) + direction));
      const card = cards[index];
      if (!card) return;
      const left = rail.scrollLeft + card.getBoundingClientRect().left - rail.getBoundingClientRect().left;
      if (typeof rail.scrollTo === 'function') rail.scrollTo({ left, behavior: this.media?.matches ? 'instant' : 'smooth' });
      else rail.scrollLeft = left;
    }
    async onClick(event) {
      const day = event.target.closest('[data-holiday-calendar-day]');
      if (day) { if (day.getAttribute('aria-disabled') === 'true') { const reason = this.root.querySelector('[data-holiday-date-reason]'); if (reason) reason.textContent = day.title; this.announce(day.title); return; } this.root.querySelector('[name="holiday_date"]').value = day.dataset.holidayCalendarDay; this.applyDeliveryDraft(); return; }
      const month = event.target.closest('[data-holiday-month]');
      if (month) { this.changeMonth(Number(month.dataset.holidayMonth)); return; }
      const filter = event.target.closest('[data-holiday-filter]');
      if (filter && this.root.contains(filter)) {
        const { format, category } = filter.dataset;
        if (!this.filters[format] || !(category === 'all' || PAIRS[format].includes(category))) return;
        const filterBar = filter.closest('.holiday-edit__filters'), filterRect = filterBar?.getBoundingClientRect();
        const wasPinned = filterRect?.height > 0 && filterRect.top <= this.pageScrollOffset(true);
        this.filters[format] = category; this.renderFilters();
        const rail = filter.closest('[data-holiday-chapter]')?.querySelector('[data-holiday-product-rail]');
        if (rail) { rail.scrollLeft = 0; this.updateProductRail(rail); }
        if (wasPinned) {
          const chapter = filter.closest('[data-holiday-chapter]');
          const result = window.matchMedia('(max-width: 749px)').matches
            ? chapter?.querySelector('[data-holiday-rail-controls]')
            : chapter?.querySelector('[data-holiday-card]:not([hidden])');
          // The filter rail attaches flush to navigation; the usual 12px reading gutter would expose a heading sliver.
          const pinnedTop = Math.max(0, this.pageScrollOffset(true) - 12);
          if (result) window.scrollTo({ top: Math.max(0, window.scrollY + result.getBoundingClientRect().top - pinnedTop - filterRect.height), behavior: this.media?.matches ? 'instant' : 'smooth' });
        }
        const url = new URL(window.location.href), param = format === 'party-tray' ? 'tray_category' : 'packed_category';
        if (category === 'all') url.searchParams.delete(param); else url.searchParams.set(param, category);
        window.history.pushState(null, '', url); return;
      }
      const button = event.target.closest('[data-holiday-action]'); if (!button || !this.root.contains(button) || button.disabled) return;
      const action = button.dataset.holidayAction, key = button.dataset.dishKey;
      if (action === 'rail-previous' || action === 'rail-next') { this.moveProductRail(button, action === 'rail-next' ? 1 : -1); return; }
      if (['add', 'increase', 'decrease'].includes(action)) {
        const dish = this.dishes.find(item => item.key === key), q = this.selection[key] || 0;
        const variant = this.delivery ? dish?.variants[this.delivery.window] : Object.values(dish?.variants || {}).find(item => item.available);
        const step = variant?.quantityRule?.increment || 1, minimum = variant?.quantityRule?.minimum || 1;
        this.setQuantity(key, action === 'decrease' ? (q <= minimum ? 0 : Math.max(0, q - step)) : this.nextQuantity(dish), { keyboard: event.detail === 0 });
      } else if (action === 'open-bag') { this.openDialog(this.root.querySelector('[data-holiday-bag]'), button); this.refreshRegularContext(); if (readOnlyControllers.has(this) && !this.deliveryPending && !this.proposedDelivery) await this.refreshPreview(); }
      else if (action === 'close-bag') this.closeDialog(this.root.querySelector('[data-holiday-bag]'));
      else if (action === 'edit-delivery') this.openDelivery(button);
      else if (action === 'delivery-retry') { if (!this.ready) await this.load(); if (this.ready) await this.applyDeliveryDraft(this.proposedDelivery || this.readDeliveryFields()); }
      else if (action === 'bag-primary') {
        if (!Object.keys(this.selection).length) this.closeDialog(this.root.querySelector('[data-holiday-bag]'));
        else if (!this.ready) { await this.load(); if (this.ready) await this.applyDeliveryDraft(); }
        else if (this.deliveryMissingField()) this.focusDeliveryField();
        else if (this.proposedDelivery || this.error || !validateDelivery(this.delivery, this.config, this.operations, this.clock()).valid) await this.applyDeliveryDraft(this.proposedDelivery || this.readDeliveryFields());
        else if (readOnlyControllers.has(this)) return;
        else if (!this.hasTermsAcceptance()) { this.handleError(fail('termsRequired')); this.root.querySelector('[data-holiday-terms-accept]')?.focus(); }
        else if (!this.hasCurrentConsent()) await this.submit();
        else await this.checkout();
      } else if (action === 'review') await this.submit();
      else if (action === 'checkout') await this.checkout();
      else if (action === 'retry') await this.retry();
    }
    readDeliveryFields() {
      const field = name => this.root.querySelector(`[name="holiday_${name}"]`)?.value || '';
      return { location: field('location'), date: field('date') };
    }
    writeDeliveryFields(slot) {
      for (const field of ['location', 'date']) { const node = this.root.querySelector(`[name="holiday_${field}"]`); if (node) node.value = slot?.[field] || ''; }
    }
    populateLocations() {
      const select = this.root.querySelector('[name="holiday_location"]'); if (!select) return;
      const value = select.value;
      select.querySelectorAll('option[value]:not([value=""])').forEach(option => option.remove());
      for (const location of this.operations.locations) { const option = document.createElement('option'); option.value = location.location_slug; option.textContent = location.location_name; select.append(option); }
      select.disabled = false; select.value = value; this.updateDeliveryOptions();
    }
    dateReason(date) {
      if (!this.ready) return this.text(this.error || 'operationalError');
      const dateError = dateEligibility(date, null, this.clock());
      if (dateError) return this.text(dateError);
      if (arraySetting(this.config.campaign.closures).includes(date)) return this.text('closedDate');
      const slot = this.readDeliveryFields();
      const locations = this.operations.locations.filter(location => !slot.location || location.location_slug === slot.location).map(location => location.location_slug);
      if (!locations.length) return this.text('noAreas');
      const results = locations.map(location => resolveDelivery({ location, date }, this.config, this.operations, this.clock(), this.selection, this.dishes));
      return results.some(result => result.valid) ? '' : this.text(results.some(result => result.error === 'unavailableSelection') ? 'unavailableSelection' : 'closedDate');
    }
    updateDeliveryOptions() { this.renderCalendar(); }
    renderCalendar(focusDate) {
      const grid = this.root.querySelector('[data-holiday-calendar-grid]'); if (!grid) return;
      const selected = this.readDeliveryFields().date;
      const availability = this.ready ? firstAvailableDeliveryDate(this.config, this.operations, this.clock(), this.readDeliveryFields().location, this.selection, this.dishes) : { date: null, error: this.error || 'operationalError' };
      if (!this.calendarMonth) this.calendarMonth = (selected && !this.dateReason(selected) ? selected : availability.date || manilaToday(this.clock())).slice(0, 7) + '-01';
      if (!parseISODate(this.calendarMonth)) return; const date = new Date(`${this.calendarMonth}T12:00:00Z`);
      const active = grid.contains(document.activeElement) ? document.activeElement.dataset.holidayCalendarDay : null;
      const label = this.root.querySelector('[data-holiday-month-label]');
      label.textContent = new Intl.DateTimeFormat(this.config.locale || 'en-PH', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
      grid.replaceChildren();
      const start = addDays(this.calendarMonth, -date.getUTCDay());
      const firstEligible = Array.from({ length: 31 }, (_, index) => addDays(this.calendarMonth, index)).find(iso => iso.slice(0, 7) === this.calendarMonth.slice(0, 7) && !this.dateReason(iso));
      const empty = this.root.querySelector('[data-holiday-calendar-empty]'); if (empty) { empty.hidden = !this.ready || !!firstEligible; empty.textContent = this.text(availability.error || 'noDatesMonth'); }
      const preferred = focusDate || active || (selected.startsWith(this.calendarMonth.slice(0, 7)) && !this.dateReason(selected) ? selected : firstEligible || this.calendarMonth);
      for (let i = 0; i < 42; i++) {
        const iso = addDays(start, i), node = document.createElement('button'); node.type = 'button'; node.className = 'holiday-edit__calendar-day'; node.dataset.holidayCalendarDay = iso;
        const outside = iso.slice(0, 7) !== this.calendarMonth.slice(0, 7); node.dataset.outside = String(outside);
        const reason = outside ? this.text('otherMonth') : this.dateReason(iso);
        node.textContent = String(Number(iso.slice(8))); node.tabIndex = iso === preferred ? 0 : -1;
        node.setAttribute('aria-disabled', String(!!reason)); node.setAttribute('aria-pressed', String(selected === iso));
        node.setAttribute('aria-label', `${canonicalDate(iso)}${reason ? ': ' + reason : ''}`); node.title = reason || canonicalDate(iso); grid.append(node);
      }
      if (!grid.querySelector('[tabindex="0"]')) grid.querySelector('[data-outside="false"]').tabIndex = 0;
      if (focusDate || active) grid.querySelector(`[data-holiday-calendar-day="${focusDate || active}"]`)?.focus({ preventScroll: true });
    }
    changeMonth(amount, focusDate) {
      if (!parseISODate(this.calendarMonth)) return; const current = new Date(`${this.calendarMonth}T12:00:00Z`);
      current.setUTCMonth(current.getUTCMonth() + amount);
      this.calendarMonth = current.toISOString().slice(0, 10); this.renderCalendar(focusDate);
      this.effect(this.root.querySelector('[data-holiday-calendar-grid]'), [{ opacity: 0 }, { opacity: 1 }], 120);
    }
    calendarKeydown(event) {
      const node = event.target.closest('[data-holiday-calendar-day]'); if (!node) return;
      const date = node.dataset.holidayCalendarDay, day = new Date(`${date}T12:00:00Z`).getUTCDay();
      let next;
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) next = addDays(date, { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, Home: -day, End: 6 - day }[event.key]);
      else if (['PageUp', 'PageDown'].includes(event.key)) { const d = new Date(`${date}T12:00:00Z`), original = d.getUTCDate(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + (event.key === 'PageUp' ? -1 : 1)); const end = new Date(d); end.setUTCMonth(end.getUTCMonth() + 1); end.setUTCDate(0); d.setUTCDate(Math.min(original, end.getUTCDate())); next = d.toISOString().slice(0, 10); }
      if (!next) return;
      event.preventDefault(); const changed = next.slice(0, 7) !== this.calendarMonth.slice(0, 7); this.calendarMonth = next.slice(0, 7) + '-01'; this.renderCalendar(next);
      if (changed) this.effect(this.root.querySelector('[data-holiday-calendar-grid]'), [{ opacity: 0 }, { opacity: 1 }], 120);
    }
    cancelDelivery() {
      this.invalidateNavigation(); this.proposedDelivery = null; this.error = null; this.writeDeliveryFields(this.delivery); this.updateDeliveryOptions(); this.finishDelivery(); this.render();
    }
    finishDelivery() {
      // Saving or cancelling updates the single visible editor; review never jumps back to the catalogue.
      this.deliveryContext = null;
      this.writeDeliveryFields(this.delivery || this.savedCandidate?.candidate);
      this.updateDeliveryOptions(); this.render(); this.measureViewport?.();
    }
    openReview(trigger = document.activeElement, focusDelivery = false) {
      const review = this.root.querySelector('[data-holiday-review]'), browse = this.root.querySelector('[data-holiday-browse]');
      if (!review || !browse) return;
      const entering = review.hidden;
      if (entering) {
        this.reviewContext = { trigger, scrollY: window.scrollY };
        this.root.dataset.holidayView = 'review'; browse.hidden = true; review.hidden = false; review.inert = false;
        this.writeDeliveryFields(this.proposedDelivery || this.delivery || this.savedCandidate?.candidate);
        this.calendarMonth = null;
      }
      const panel = review.querySelector('[data-holiday-delivery]'); if (panel) panel.hidden = false;
      this.root.querySelectorAll('[data-holiday-action="edit-delivery"]').forEach(button => button.setAttribute('aria-expanded', 'true'));
      this.updateDeliveryOptions(); this.render();
      if (entering && (this.proposedDelivery || this.savedCandidate?.candidate)) this.applyDeliveryDraft(this.proposedDelivery || this.savedCandidate.candidate);
      if (entering) this.effect(review, [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }], 180);
      const focus = focusDelivery ? panel?.querySelector('[name="holiday_location"]') : review.querySelector('[data-holiday-review-heading]');
      this.scrollToSection(entering ? review : panel, focus);
    }
    closeReview(restore = true) {
      const review = this.root.querySelector('[data-holiday-review]'), browse = this.root.querySelector('[data-holiday-browse]'), context = this.reviewContext;
      if (!review || !browse) return;
      this.reviewContext = null; this.deliveryContext = null;
      review.hidden = true; review.inert = false; browse.hidden = false; this.root.dataset.holidayView = 'browse';
      this.root.querySelectorAll('[data-holiday-action="edit-delivery"]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      this.writeDeliveryFields(this.proposedDelivery || this.delivery || this.savedCandidate?.candidate); this.render(); this.measureViewport?.();
      if (restore && context && !this.destroyed) {
        this.suppressViewportFocus = true; context.trigger?.focus?.({ preventScroll: true }); this.suppressViewportFocus = false;
        window.scrollTo({ top: context.scrollY, behavior: 'instant' });
      }
    }
    openDelivery(trigger) { this.openReview(trigger, true); }
    pageScrollOffset(includeNavigation = true) {
      const nodes = [document.querySelector('.section-header') || document.querySelector('.shopify-section-header-sticky') || document.querySelector('sticky-header'), this.root.querySelector('.holiday-edit__utility'), includeNavigation ? this.root.querySelector('.holiday-edit__navigation') : null];
      return 12 + nodes.reduce((height, node) => height + (node && getComputedStyle(node).display !== 'none' && ['fixed', 'sticky'].includes(getComputedStyle(node).position) ? node.getBoundingClientRect().height : 0), 0);
    }
    scrollToSection(target, focusTarget = target) {
      if (!target) return;
      this.suppressViewportFocus = true;
      if (focusTarget) { if (focusTarget === target) target.tabIndex = -1; focusTarget.focus({ preventScroll: true }); }
      this.suppressViewportFocus = false; this.lastMeasuredFocus = focusTarget;
      window.scrollTo({ top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - this.pageScrollOffset(target.hasAttribute('data-holiday-chapter'))), behavior: this.media?.matches ? 'instant' : 'smooth' });
    }
    effect(node, frames, duration) {
      if (!node) return;
      this.effects.get(node)?.cancel(); this.effects.delete(node);
      if (this.media?.matches || !node.animate || this.destroyed) return;
      try { const animation = node.animate(frames, { duration, easing: 'cubic-bezier(.23,1,.32,1)' }); this.effects.set(node, animation); animation.finished.then(() => { if (this.effects.get(node) === animation) this.effects.delete(node); }).catch(() => {}); } catch (_) { /* Final semantic state is already usable. */ }
    }
    openDialog(dialog, trigger = document.activeElement) {
      if (!dialog) return;
      if (dialog.matches('[data-holiday-review]')) return this.openReview(trigger);
      this.closingViews.forEach(node => { this.effects.get(node.firstElementChild)?.cancel(); this.effects.delete(node.firstElementChild); node.remove(); }); this.closingViews.clear();
      this.root.querySelectorAll('dialog[open]').forEach(other => { if (other !== dialog) this.closeDialog(other, false); });
      this.effects.get(dialog)?.cancel(); dialog.inert = false; dialog._holidayReturnFocus = trigger;
      if (typeof dialog.showModal === 'function') { if (!dialog.open) dialog.showModal(); }
      else { dialog.hidden = false; dialog.setAttribute('open', ''); dialog.setAttribute('aria-modal', 'true'); }
      this.root.dataset.holidayModalOpen = 'true';
      const bag = dialog.matches('[data-holiday-bag]'), mobile = window.matchMedia?.('(max-width: 749px)').matches;
      const offset = bag ? (mobile ? 'translateY(16px)' : 'translateX(24px)') : 'translateY(8px)';
      this.effect(dialog, [{ opacity: 0, transform: offset }, { opacity: 1, transform: 'translate(0)' }], bag ? 220 : 180);
      dialog.querySelector('button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]')?.focus({ preventScroll: true });
      this.measureViewport?.();
    }
    closeDialog(dialog, restore = true) {
      if (!dialog) return;
      if (dialog.matches('[data-holiday-review]')) return this.closeReview(restore);
      if (dialog.open && !this.media?.matches && !this.destroyed) this.closeDecoration(dialog);
      dialog.inert = true; this.effects.get(dialog)?.cancel();
      if (typeof dialog.close === 'function') dialog.close(); else { dialog.removeAttribute('open'); dialog.hidden = true; }
      this.root.dataset.holidayModalOpen = String(!!this.root.querySelector('dialog[open]'));
      const trigger = dialog._holidayReturnFocus; if (restore && trigger?.isConnected) trigger.focus({ preventScroll: true });
      this.measureViewport?.();
    }
    closeDecoration(dialog) {
      // A noninteractive, inaccessible paint snapshot lets native close/focus finish now.
      // It has no holiday hooks or IDs, so it can never become a second state owner.
      const rect = dialog.getBoundingClientRect(), wrapper = document.createElement('div'); wrapper.className = 'holiday-edit'; wrapper.setAttribute('aria-hidden', 'true'); wrapper.inert = true;
      const snapshot = dialog.cloneNode(true); snapshot.removeAttribute('open'); snapshot.removeAttribute('aria-modal');
      [snapshot, ...snapshot.querySelectorAll('*')].forEach(node => { for (const attr of [...node.attributes]) if (attr.name === 'id' || attr.name.startsWith('data-holiday-') || attr.name.startsWith('aria-')) node.removeAttribute(attr.name); });
      Object.assign(snapshot.style, { display: 'flex', position: 'fixed', margin: '0', inset: 'auto', left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px`, maxHeight: 'none', pointerEvents: 'none', zIndex: '10000' });
      wrapper.append(snapshot); document.body.append(wrapper); this.closingViews.add(wrapper);
      const bag = dialog.matches('[data-holiday-bag]'), mobile = window.matchMedia?.('(max-width: 749px)').matches;
      const offset = bag ? (mobile ? 'translateY(16px)' : 'translateX(24px)') : 'translateY(8px)';
      this.effect(snapshot, [{ opacity: 1, transform: 'translate(0)' }, { opacity: 0, transform: offset }], bag ? 180 : 140);
      const animation = this.effects.get(snapshot), cleanup = () => { wrapper.remove(); this.closingViews.delete(wrapper); };
      if (animation) animation.finished.then(cleanup, cleanup); else cleanup();
    }
    observeViewport() {
      const chatStyle = document.body.style, previousChatBottom = chatStyle.getPropertyValue('--holiday-chat-bottom'), previousChatPriority = chatStyle.getPropertyPriority('--holiday-chat-bottom');
      let writtenChatBottom = null;
      this.cleanups.push(() => { if (chatStyle.getPropertyValue('--holiday-chat-bottom') === writtenChatBottom) { if (previousChatBottom) chatStyle.setProperty('--holiday-chat-bottom', previousChatBottom, previousChatPriority); else chatStyle.removeProperty('--holiday-chat-bottom'); } });
      this.measureViewport = () => {
        if (this.measuring || this.destroyed) return; this.measuring = true;
        try {
          const viewport = window.visualViewport, height = viewport?.height || window.innerHeight;
          const pinned = node => node && !node.hidden && !node.closest('[hidden]') && getComputedStyle(node).display !== 'none' && ['fixed', 'sticky'].includes(getComputedStyle(node).position) ? node.getBoundingClientRect().height : 0;
          const header = document.querySelector('.section-header') || document.querySelector('.shopify-section-header-sticky') || document.querySelector('sticky-header');
          const strip = this.root.querySelector('.holiday-edit__utility'), bottom = this.root.querySelector('.holiday-edit__mobile-bag'), footer = this.root.querySelector('dialog[open] [data-holiday-bag-footer]');
          const composer = this.root.querySelector('.holiday-edit__composer');
          header?.removeAttribute('data-holiday-header-flow');
          if (strip) strip.dataset.holidayStickyVisible = String(!!composer && composer.getBoundingClientRect().bottom < (header?.getBoundingClientRect().bottom || 0));
          this.root.dataset.holidayStripUnpinned = 'false'; this.root.dataset.holidayFooterFlow = 'false';
          const chapterHeight = pinned(this.root.querySelector('.holiday-edit__navigation'));
          let top = pinned(header) + pinned(strip) + chapterHeight, low = (footer && !footer.hidden ? footer.getBoundingClientRect().height : 0) + (this.root.dataset.holidayModalOpen === 'true' ? 0 : pinned(bottom) + (bottom && !bottom.hidden && getComputedStyle(bottom).position === 'fixed' ? Math.max(0, parseFloat(getComputedStyle(bottom).bottom) || 0) : 0));
          if (top + low > height * .3) { this.root.dataset.holidayStripUnpinned = 'true'; top = pinned(header) + chapterHeight; }
          if (height < 500 && top + low > height * .3) { this.root.dataset.holidayFooterFlow = 'true'; low = 0; }
          this.root.style.setProperty('--holiday-header-height', `${pinned(header)}px`);
          this.root.style.setProperty('--holiday-occupied-top', `${top}px`); this.root.style.setProperty('--holiday-occupied-bottom', `${low}px`);
          const chatBottom = this.root.dataset.holidayModalOpen !== 'true' && this.root.dataset.holidayFooterFlow !== 'true' && bottom && !bottom.hidden && getComputedStyle(bottom).display !== 'none' && getComputedStyle(bottom).position === 'fixed' ? bottom.getBoundingClientRect().height : 0;
          writtenChatBottom = `${chatBottom}px`; chatStyle.setProperty('--holiday-chat-bottom', writtenChatBottom);
          const filters = [...this.root.querySelectorAll('.holiday-edit__filters')];
          for (const filter of filters) {
            const rect = filter.getBoundingClientRect();
            // Parent-bound sticky positioning can leave a clipped strip of the previous chapter.
            // Hide only that paint; retaining its geometry lets it return normally when scrolling back.
            const occluded = getComputedStyle(filter).position === 'sticky' && rect.height > 0 && rect.top < top - 1;
            const value = String(occluded);
            if (filter.dataset.holidayFilterOccluded !== value) filter.dataset.holidayFilterOccluded = value;
          }
          const pinnedFilters = filters.find(node => {
            const rect = node.getBoundingClientRect(), chapter = node.closest('[data-holiday-chapter]');
            return node.dataset.holidayFilterOccluded !== 'true' && pinned(node) > 0 && rect.top <= top + 1 && rect.bottom > top && chapter?.getBoundingClientRect().bottom > top;
          });
          const contentTop = top + (pinnedFilters?.getBoundingClientRect().height || 0);
          document.documentElement.style.scrollPaddingTop = `${contentTop + 12}px`; document.documentElement.style.scrollPaddingBottom = `${low + 12}px`;
          const active = document.activeElement;
          if (!this.suppressViewportFocus && active && this.root.contains(active) && active !== this.lastMeasuredFocus) { const rect = active.getBoundingClientRect(), offset = viewport?.offsetTop || 0, focusTop = pinnedFilters?.contains(active) ? top : contentTop; if (rect.top < offset + focusTop || rect.bottom > offset + height - low) active.scrollIntoView?.({ block: 'nearest', behavior: 'instant' }); this.lastMeasuredFocus = active; }
        } finally { this.measuring = false; }
      };
      const update = () => { this.lastMeasuredFocus = null; this.measureViewport(); };
      this.listen(window, 'resize', update); this.listen(window, 'scroll', this.measureViewport, { passive: true }); this.listen(window.visualViewport, 'resize', update); this.listen(window.visualViewport, 'scroll', update); this.listen(this.root, 'focusin', update);
      if (typeof ResizeObserver !== 'undefined') { const observer = new ResizeObserver(update); for (const node of [this.root.querySelector('.holiday-edit__utility'), document.querySelector('.section-header') || document.querySelector('sticky-header'), this.root.querySelector('.holiday-edit__navigation'), ...this.root.querySelectorAll('.holiday-edit__filters'), this.root.querySelector('[data-holiday-bag-footer]')]) if (node) observer.observe(node); this.cleanups.push(() => observer.disconnect()); }
      this.cleanups.push(() => { this.root.querySelectorAll('[data-holiday-filter-occluded]').forEach(node => node.removeAttribute('data-holiday-filter-occluded')); document.documentElement.style.scrollPaddingTop = ''; document.documentElement.style.scrollPaddingBottom = ''; document.querySelector('[data-holiday-header-flow]')?.removeAttribute('data-holiday-header-flow'); });
      update();
    }
    trapFocus(event) {
      if (event.target.closest('[data-holiday-guide]')) return;
      if (event.key !== 'Tab') return;
      const dialog = event.target.closest('dialog[open], [role="dialog"][open]'); if (!dialog) return;
      const nodes = Array.from(dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex="0"]')).filter(node => !node.hidden && !node.closest('[hidden]'));
      if (!nodes.length) return;
      if (event.shiftKey && document.activeElement === nodes[0]) { event.preventDefault(); nodes.at(-1).focus(); }
      else if (!event.shiftKey && document.activeElement === nodes.at(-1)) { event.preventDefault(); nodes[0].focus(); }
    }
    observeChapters() {
      const links = [...this.root.querySelectorAll('.holiday-edit__chapters a[href^="#holiday-"], [data-holiday-chapter-link]')];
      const chapters = [...this.root.querySelectorAll('[data-holiday-chapter]')];
      let clickedChapter = null;
      const activate = id => {
        for (const link of links) {
          const active = link.hash === `#${id}`, changed = active && !link.hasAttribute('aria-current');
          if (active) link.setAttribute('aria-current', 'location'); else link.removeAttribute('aria-current');
          if (changed) {
            const nav = link.closest('.holiday-edit__chapters'); if (!nav) continue;
            const bounds = nav.getBoundingClientRect(), rect = link.getBoundingClientRect();
            const delta = rect.left < bounds.left ? rect.left - bounds.left : rect.right > bounds.right ? rect.right - bounds.right : 0;
            if (delta) nav.scrollTo({ left: nav.scrollLeft + delta, behavior: this.media?.matches ? 'instant' : 'smooth' });
          }
        }
      };
      for (const link of links) this.listen(link, 'click', event => {
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = this.root.querySelector(link.hash); if (!target) return;
        event.preventDefault(); window.history.replaceState(null, '', link.hash); clickedChapter = target.id; activate(target.id); this.scrollToSection(target);
      });
      const update = () => {
        if (this.chapterFrame || this.destroyed) return;
        this.chapterFrame = window.requestAnimationFrame(() => {
          this.chapterFrame = null; if (this.destroyed) return;
          if (clickedChapter) { activate(clickedChapter); return; }
          const offset = this.pageScrollOffset() + 16;
          const current = chapters.filter(node => node.getBoundingClientRect().top <= offset).at(-1) || chapters[0];
          if (current) activate(current.id);
        });
      };
      // Keep an explicit chapter through smooth scrolling and horizontal rail interactions.
      // A tap or horizontal swipe is not intent to leave the selected chapter.
      const resumeTracking = () => { clickedChapter = null; update(); };
      this.listen(window, 'wheel', event => {
        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) resumeTracking();
      }, { passive: true });
      let touchOrigin = null;
      this.listen(window, 'touchstart', event => {
        const touch = event.touches?.[0];
        touchOrigin = touch ? { x: touch.clientX, y: touch.clientY } : null;
      }, { passive: true });
      this.listen(window, 'touchmove', event => {
        const touch = event.touches?.[0];
        if (!touch || !touchOrigin) return;
        const x = Math.abs(touch.clientX - touchOrigin.x), y = Math.abs(touch.clientY - touchOrigin.y);
        if (y > 8 && y > x) { touchOrigin = null; resumeTracking(); }
      }, { passive: true });
      for (const type of ['touchend', 'touchcancel']) this.listen(window, type, () => { touchOrigin = null; }, { passive: true });
      this.listen(window, 'keydown', event => {
        if (event.defaultPrevented || !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) return;
        if (event.target.closest?.('input, select, textarea, [contenteditable="true"]')) return;
        if (event.key === ' ' && event.target.closest?.('button, summary, [role="button"]')) return;
        resumeTracking();
      });
      // Native scrollbar drags do not generate wheel or touch movement.
      this.listen(window, 'pointerdown', event => {
        const width = document.documentElement.clientWidth;
        if (width > 0 && event.clientX >= width) resumeTracking();
      }, { passive: true });
      this.listen(window, 'scroll', update, { passive: true }); this.listen(window, 'resize', resumeTracking); update();
      this.cleanups.push(() => window.cancelAnimationFrame(this.chapterFrame));
    }
    observeRegularHeader() {
      const update = () => {
        const link = document.querySelector('#cart-icon-bubble'); if (!link) return; link.classList.remove('holiday-edit-regular-cart');
        const count = link.querySelector('[data-cart-count]')?.dataset.cartCount || link.dataset.cartCount || link.querySelector('.cart-count-bubble [aria-hidden="true"]')?.textContent?.trim() || String(this.regularCount || 0);
        const label = count === '1' ? (this.config.strings?.regularCartNameOne || 'Regular cart, {count} item') : (this.config.strings?.regularCartName || 'Regular cart, {count} items');
        const next = label.replace('{count}', count); if (link.getAttribute('aria-label') !== next) link.setAttribute('aria-label', next);
        link.querySelector('[data-holiday-regular-label]')?.remove();
      };
      update();
      if (typeof MutationObserver !== 'undefined') { const observer = new MutationObserver(update); const link = document.querySelector('#cart-icon-bubble'); const header = link?.closest('.shopify-section-group-header-group') || link?.closest('header'); if (header) observer.observe(header, { childList: true, subtree: true }); this.cleanups.push(() => observer.disconnect()); }
    }
    async refreshRegularContext() {
      try { const response = await this.fetch('/cart.js', { cache: 'no-store' }); if (!response.ok) return; const cart = await response.json(); if (this.destroyed) return; this.root.querySelectorAll('[data-holiday-regular-notice]').forEach(node => { node.hidden = !(cart.item_count > 0); }); } catch (_) { /* Regular cart remains independent. */ }
    }
    destroy() {
      if (this.destroyed) return;
      this.destroyed = true; this.revision++; this.cancelAnimations(); clearTimeout(this.announcementTimer);
      this.cleanups.forEach(cleanup => cleanup()); this.cleanups = [];
      this.consentContext.cancelAllergenPrompt?.();
      if (this.reviewContext) this.closeReview(false);
      if (this.deliveryContext) this.finishDelivery();
      this.root.querySelectorAll('dialog[open]').forEach(dialog => this.closeDialog(dialog));
      privateCarts.delete(this); this.consent = null; this.confirmed = null;
    }
  }

  const helpers = Object.freeze({ normalizeCatalog, moneyToCents, validQuantity, parseISODate, addDays, manilaToday, consumptionDate, dateEligibility, firstAvailableDeliveryDate, resolveDelivery, isPreviewConfig, isCatalogueConfig, validateConfig, validateDelivery, deriveLines, readSelection, readDeliveryCandidate, saveSelection, validateCheckoutUrl, verifyCart });
  window.NumMealsHoliday = Object.freeze({ helpers, StorefrontAdapter, Controller });
  function mount(scope = document) {
    const roots = [...(scope.matches?.('[data-holiday-edit]') ? [scope] : []), ...scope.querySelectorAll('[data-holiday-edit]')];
    for (const root of roots) {
      if (instances.has(root)) continue;
      try {
        const config = JSON.parse(root.querySelector('[data-holiday-config]').textContent);
        const controller = new Controller(root, config); instances.set(root, controller); controller.init();
      } catch (_) {
        const status = root.querySelector('[data-holiday-status]'); if (status) status.textContent = root.dataset.holidayConfigurationError || fallback.configuration;
      }
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true }); else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const roots = [...(event.target.matches?.('[data-holiday-edit]') ? [event.target] : []), ...event.target.querySelectorAll('[data-holiday-edit]')];
    roots.forEach(root => { instances.get(root)?.destroy(); instances.delete(root); });
  });
})();
